import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Button,
  useMediaQuery,
  Alert,
  Box,
  Typography,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Paper,
  Chip,
  IconButton,
  FormControl,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Divider,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  AutoAwesome,
  Close,
  CheckCircle,
  ArrowBack,
  ArrowForward,
  Delete,
  Add,
  CloudUpload,
  FactCheck,
  PhotoLibrary,
  InsertDriveFile,
  EventAvailable
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { brand } from '../../theme/colors';
import BidDocumentPreview from './BidDocumentPreview';
import DialogCloseButton from '../common/DialogCloseButton';
import { formatHarare } from '../../utils/time';

const FIXED_PLATFORM_TERMS = `1. GENERAL AGREEMENT: These Terms and Conditions constitute the entire agreement between the Contractor and the Client for the provision of services at the specified location.

2. CLIENT RESPONSIBILITIES: The Client shall provide clear access to work areas and ensure the availability of water and electricity at no cost to the Contractor. Valuable or fragile items must be removed from the vicinity before work commences.

3. PAYMENT & SECURITY: In alignment with platform standards, the Project Balance shall be held securely and released to the Contractor immediately upon verified completion of the work.

4. CHANGE ORDERS: Any request for work outside the defined Scope of Work must be documented in a written Change Order. No additional work will commence until the revised cost and schedule are approved by the Client.

5. WARRANTIES: The Contractor provides a 12-month warranty on labor, covering defects in application. Standard manufacturer warranties apply to all materials used.

6. LIABILITY: The Contractor is not liable for indirect or incidental damages or damage to personal property not removed from the work area.`;

const COST_BREAKDOWN_FIELDS = [
  { key: 'materials', label: 'Materials' },
  { key: 'labor', label: 'Labor' },
  { key: 'demolition', label: 'Demolition / Prep' },
  { key: 'plumbing', label: 'Plumbing' },
  { key: 'electrical', label: 'Electrical' },
  { key: 'cabinetry', label: 'Cabinetry / Carpentry' },
  { key: 'tiling', label: 'Tiling / Flooring' },
  { key: 'finishes', label: 'Finishes / Painting' },
  { key: 'equipment', label: 'Equipment' },
  { key: 'subcontractors', label: 'Subcontractors' },
  { key: 'permits', label: 'Permits' },
  { key: 'transport', label: 'Transport / Logistics' },
  { key: 'overhead', label: 'Overhead' },
  { key: 'contingency', label: 'Contingency' }
];

const getEmptyCostBreakdown = () => COST_BREAKDOWN_FIELDS.reduce((acc, field) => {
  acc[field.key] = 0;
  return acc;
}, {});

const PROJECT_TYPE_DISPLAY = {
  residential: 'Residential',
  commercial: 'Commercial',
  renovation: 'Renovation',
  infrastructure: 'Infrastructure',
  new_build: 'New build',
  extension: 'Extension',
  repair: 'Repair',
  maintenance: 'Maintenance'
};

// Cost categories always shown; the rest appear via "+ Add" chips (or when
// they already carry a value or line items from a draft/AI generation).
const CORE_COST_KEYS = ['materials', 'labor', 'transport', 'contingency'];

const emptyLineItem = () => ({ description: '', quantity: 1, unit: 'item', unitPrice: 0, priceAtPurchase: false });

// Items flagged priceAtPurchase are quantity-only — prices move too fast to
// quote, so they carry no rate and stay out of the bid total.
const lineItemTotal = (item) =>
  item.priceAtPurchase ? 0 : (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);

const categoryItemsTotal = (items = []) =>
  items.reduce((sum, item) => sum + lineItemTotal(item), 0);

const countAtPurchase = (items = []) => items.filter((item) => item.priceAtPurchase).length;

const stagesTotal = (stages = []) =>
  stages.reduce((sum, stage) => sum + (parseFloat(stage.amount) || 0), 0);

// Stage plan rule — keep in step with backend StageFundingService.
const STAGES_REQUIRED_FROM = 1000;
const FIRST_STAGE_MAX_SHARE = 0.4;
const fmtMoney = (n, currency = 'USD') => `${currency} ${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const cleanStages = (stages = []) => stages.filter((s) => String(s?.name || '').trim() && (parseFloat(s?.amount) || 0) > 0);

const STAGES_REQUIRED_FROM_WHEN_ASKED = 300;
const stagePlanProblem = (stages, total, currency = 'USD', clientWantsStages = false) => {
  const t = parseFloat(total) || 0;
  const cleaned = cleanStages(stages);
  const requiredFrom = clientWantsStages ? STAGES_REQUIRED_FROM_WHEN_ASKED : STAGES_REQUIRED_FROM;
  if (cleaned.length === 0) {
    if (t < requiredFrom) return null;
    return clientWantsStages && t < STAGES_REQUIRED_FROM
      ? `This client asked to pay in stages. Split the ${fmtMoney(t, currency)} into at least two payment stages so they can fund the first one and start.`
      : `Quotations of ${fmtMoney(STAGES_REQUIRED_FROM, currency)} or more must be split into payment stages (at least two), so the client can fund the first stage and start rather than the whole ${fmtMoney(t, currency)} at once.`;
  }
  const sum = stagesTotal(cleaned);
  if (t > 0 && Math.abs(sum - t) > 0.01) {
    return `Your payment stages add up to ${currency} ${sum.toLocaleString('en-US', { minimumFractionDigits: 2 })} but the quotation total is ${currency} ${t.toLocaleString('en-US', { minimumFractionDigits: 2 })} — they must match exactly.`;
  }
  if (t >= requiredFrom) {
    if (cleaned.length < 2) return `A quotation of ${fmtMoney(t, currency)} needs at least two payment stages, so the client can start with the first one.`;
    const share = (parseFloat(cleaned[0].amount) || 0) / (t || sum);
    if (share > FIRST_STAGE_MAX_SHARE + 0.0001) {
      return `Your first stage ("${cleaned[0].name}") is ${Math.round(share * 100)}% of the total. Keep the first stage to ${Math.round(FIRST_STAGE_MAX_SHARE * 100)}% or less (${fmtMoney((t || sum) * FIRST_STAGE_MAX_SHARE, currency)}) — a smaller first step is what gets a client to say yes.`;
    }
  }
  return null;
};


// The AI sometimes returns a narrative section as an array or keyed object
// instead of a string — flatten to readable text instead of "[object Object]".
const toSectionText = (value) => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(toSectionText).filter(Boolean).join('\n\n');
  if (value && typeof value === 'object') {
    return Object.entries(value)
      .map(([key, entry]) => {
        const text = toSectionText(entry);
        if (!text) return '';
        const heading = String(key).replaceAll('_', ' ').replace(/^\w/, (c) => c.toUpperCase());
        return /^\d+$/.test(key) ? text : `${heading}:\n${text}`;
      })
      .filter(Boolean)
      .join('\n\n');
  }
  return value == null ? '' : String(value);
};

const normalizeSelectableItems = (items = [], selectedByDefault = true) => (
  Array.isArray(items)
    ? items
      .map((item) => {
        if (typeof item === 'string') {
          return { text: item, selected: selectedByDefault, custom: false };
        }

        if (item && typeof item === 'object') {
          return {
            text: item.text || item.label || item.value || '',
            selected: item.selected !== false,
            custom: !!item.custom
          };
        }

        return null;
      })
      .filter((item) => item?.text?.trim())
    : []
);

const getSelectedItemTexts = (items = []) => normalizeSelectableItems(items)
  .filter((item) => item.selected)
  .map((item) => item.text);

const BID_SETUP_HELP = {
  bidApproach: {
    full_service: 'You will manage and deliver the whole job, including labor, coordination, materials handling, subcontractors, cleanup, and the finished result.',
    labor_only: 'You are pricing workmanship only. The client supplies the main materials, or materials will be priced separately.',
    assessment_first: 'You need to inspect the site or confirm measurements before giving a final fixed price. AI will frame this as an initial assessment-based quotation.',
    milestone_only: 'You are quoting for one phase only, such as demolition, plumbing, cabinets, tiling, roofing, foundation, or finishing.'
  },
  materialsResponsibility: {
    contractor_supplied: 'Your quote includes sourcing and supplying the materials needed for the job.',
    client_supplied: 'The client provides the main materials. Your price should focus on labor, coordination, and installation.',
    mixed_materials: 'Some materials are supplied by you and some by the client. Use Notes for AI to clarify which items belong to each party.'
  },
  siteVisitRequirement: {
    recommended_before_start: 'Your quotation can be submitted now, but you recommend confirming site conditions before work begins.',
    required_before_quote: 'You are not comfortable giving a final fixed price until you inspect the site first.',
    not_required: 'You have enough information to quote without a site visit.'
  },
  paymentStructure: {
    milestone_based: 'Payment should be released in phases as agreed milestones are completed.',
    deposit_then_completion: 'You want an upfront mobilization deposit, with the remaining balance released after completion.',
    monthly_progress: 'Best for longer jobs where payment is requested against monthly progress.'
  }
};

const PRICING_EVIDENCE_ITEMS = [
  {
    key: 'siteVisitCompleted',
    label: 'I have visited or inspected the site',
    helper: 'Best basis for a fixed kitchen or house renovation price.'
  },
  {
    key: 'photosReviewed',
    label: 'I have reviewed current site photos',
    helper: 'Photos should show the actual room, access, condition, and services.'
  },
  {
    key: 'measurementsConfirmed',
    label: 'Measurements or drawings are available',
    helper: 'Cabinet runs, countertop area, plumbing points, and electrical points are known.'
  },
  {
    key: 'materialSpecsConfirmed',
    label: 'Material and finish choices are clear',
    helper: 'Cabinet style, countertop type, appliances, fixtures, tiles, paint, and lighting are defined.'
  }
];

const getEmptyPricingEvidence = () => PRICING_EVIDENCE_ITEMS.reduce((acc, item) => {
  acc[item.key] = false;
  return acc;
}, {});

const safeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return value.startsWith('{') && value.endsWith('}')
        ? value.slice(1, -1).split(',').filter(Boolean)
        : [];
    }
  }
  return [];
};

/**
 * AI Bid Generator Component
 *
 * 5-step wizard for creating professional construction bids with AI assistance,
 * including an optional per-category itemized BOQ in the cost breakdown.
 *
 * Features:
 * - Complete AI generation from project description
 * - Inline AI improvement for all sections
 * - Professional Document Preview
 * - File Uploads (Mocked)
 * - Escrow-centric Payment Logic
 */
function AIBidGenerator({
  open,
  onClose,
  projectId,
  clientId,
  projectContext = {},
  onBidCreated,
  onRequestAssessment,
  existingBid = null // For edit mode
}) {
  // Auth and theme hooks
  const { user, getApiClient } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // First-timer guidance
  const [showBidGuide, setShowBidGuide] = useState(
    !localStorage.getItem('eqaya_contractor_bid_guide_seen')
  );

  // UI State
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [savedDraft, setSavedDraft] = useState(false);
  const [serverDraftId, setServerDraftId] = useState(existingBid?.id || null);
  const [pricingDecision, setPricingDecision] = useState(existingBid ? 'ready' : null);

  // "Upload your own quotation" fast path — contractor sends their own
  // letterhead document; the typed total/validity are the binding terms.
  const [uploadMode, setUploadMode] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTotal, setUploadTotal] = useState('');
  const [uploadCurrency, setUploadCurrency] = useState('USD');
  const [uploadValidUntil, setUploadValidUntil] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadConfirmed, setUploadConfirmed] = useState(false);
  // AI prefill: file uploads on selection, then extraction fills empty fields.
  const [uploadStoredFile, setUploadStoredFile] = useState(null); // server {key,...}
  const [uploadPhase, setUploadPhase] = useState('idle'); // idle|uploading|extracting|ready
  const [uploadExtraction, setUploadExtraction] = useState(null);
  // Cover sheet: the structured layer on top of the PDF. Prefilled from the
  // extraction, confirmed by the contractor, shown to the client next to the
  // document and snapshotted with the acceptance.
  const [uploadDuration, setUploadDuration] = useState('');
  const [uploadDurationUnit, setUploadDurationUnit] = useState('weeks');
  const [uploadStartCondition, setUploadStartCondition] = useState('Within 2 weeks of the first stage being funded');
  const [uploadInclusions, setUploadInclusions] = useState('');   // one per line
  const [uploadExclusions, setUploadExclusions] = useState('');   // one per line
  const [uploadMaterials, setUploadMaterials] = useState('contractor_supplied');
  const linesOf = (text) => String(text || '').split('\n').map((l) => l.replace(/^[-•*]\s*/, '').trim()).filter(Boolean).slice(0, 12);
  const [projectEvidence, setProjectEvidence] = useState({
    loading: false,
    photos: [],
    documents: [],
    files: [],
    error: ''
  });

  // Edit mode detection
  const isEditMode = !!existingBid;

  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Project selection (when no projectId provided)
  const [availableProjects, setAvailableProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProjectOption, setSelectedProjectOption] = useState(null);
  const [internalProjectId, setInternalProjectId] = useState(projectId);
  const [internalClientId, setInternalClientId] = useState(clientId);
  const [internalProjectContext, setInternalProjectContext] = useState(projectContext);

  // Step 1: Project Type & Description
  const [projectType, setProjectType] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [bidIntent, setBidIntent] = useState('priced_bid');
  const [pricingEvidence, setPricingEvidence] = useState(getEmptyPricingEvidence());
  const [bidApproach, setBidApproach] = useState('full_service');
  const [materialsResponsibility, setMaterialsResponsibility] = useState('contractor_supplied');
  const [siteVisitRequirement, setSiteVisitRequirement] = useState('recommended_before_start');
  const [paymentStructure, setPaymentStructure] = useState('milestone_based');
  const [bidNotes, setBidNotes] = useState('');

  // Field-level validation errors
  const [fieldErrors, setFieldErrors] = useState({
    projectType: '',
    projectDescription: '',
    executiveSummary: '',
    projectUnderstanding: '',
    scopeOfWork: '',
    totalCost: '',
    estimatedDuration: '',
    estimatedStartDate: '',
    depositValue: '',
    validUntil: '',
    projectSpecificTerms: ''
  });

  const setFieldError = (field, message) => {
    setFieldErrors(prev => ({ ...prev, [field]: message }));
  };

  const clearFieldError = (field) => {
    setFieldErrors(prev => ({ ...prev, [field]: '' }));
  };

  const clearAllFieldErrors = () => {
    setFieldErrors({
      projectType: '',
      projectDescription: '',
      executiveSummary: '',
      projectUnderstanding: '',
      scopeOfWork: '',
      totalCost: '',
      estimatedDuration: '',
      estimatedStartDate: '',
      depositValue: '',
      validUntil: '',
      projectSpecificTerms: ''
    });
  };

  // Step 2: Executive Summary & Understanding
  const [executiveSummary, setExecutiveSummary] = useState('');
  const [projectUnderstanding, setProjectUnderstanding] = useState('');

  // Step 3: Scope of Work
  const [scopeOfWork, setScopeOfWork] = useState('');

  // Step 4: Inclusions, Exclusions, Attachments
  const [inclusions, setInclusions] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [assumptions, setAssumptions] = useState([]);
  const [newInclusion, setNewInclusion] = useState('');
  const [newExclusion, setNewExclusion] = useState('');
  const [newAssumption, setNewAssumption] = useState('');
  const [attachments, setAttachments] = useState([]);

  // Step 5: Cost Breakdown
  const [totalCost, setTotalCost] = useState(0);
  const [costBreakdown, setCostBreakdown] = useState(getEmptyCostBreakdown()); // All cost fields optional
  // Optional itemized BOQ per category: { [categoryKey]: [{description, quantity, unit, unitPrice}] }
  const [costLineItems, setCostLineItems] = useState({});
  const [visibleCostKeys, setVisibleCostKeys] = useState(CORE_COST_KEYS);
  const [currency, setCurrency] = useState('USD');

  // Step 6: Timeline & Milestones
  const [estimatedDuration, setEstimatedDuration] = useState('');
  const [durationUnit, setDurationUnit] = useState('weeks');
  const [estimatedStartDate, setEstimatedStartDate] = useState('');
  
  // Payment stages — money is escrowed and released stage by stage, so the
  // owner never parks the full total and the contractor never starts an
  // unfunded stage. [{ name, description, amount }]
  const [paymentStages, setPaymentStages] = useState([]);

  // Step 7: Deposit & Terms
  const [depositAmount, setDepositAmount] = useState(0);
  const [depositType, setDepositType] = useState('amount'); // 'amount' or 'percentage'
  const [depositValue, setDepositValue] = useState('');
  const [projectSpecificTerms, setProjectSpecificTerms] = useState('');
  const [warrantyPeriod, setWarrantyPeriod] = useState('12 months');
  const [validUntil, setValidUntil] = useState('');

  const activeProjectContext = projectContext.name ? projectContext : internalProjectContext;
  const assessmentContext = activeProjectContext?.assessmentRequest || null;
  const siteVisitDate = assessmentContext?.scheduled_site_visit_date || assessmentContext?.accepted_window || null;
  const siteVisitStatus = assessmentContext?.scheduled_site_visit_status || assessmentContext?.status || null;
  const siteVisitHasPassed = siteVisitDate ? new Date(siteVisitDate).getTime() < Date.now() : false;
  const siteVisitCompleted = ['completed', 'site_visit_completed'].includes(siteVisitStatus);
  const ownerProvidedItems = safeArray(assessmentContext?.owner_provided_items);
  const assessmentVisitPhotoCount = safeArray(assessmentContext?.visit_photo_urls).length;
  const ownerSuppliedPhotos = projectEvidence.photos.length;
  const ownerSuppliedDocs = projectEvidence.documents.length;
  const hasOwnerSuppliedEvidence = ownerSuppliedPhotos > 0 || ownerSuppliedDocs > 0 || ownerProvidedItems.length > 0;

  // Computed terms for preview/submission
  const fullTerms = `${FIXED_PLATFORM_TERMS}${projectSpecificTerms ? `\n\nPROJECT-SPECIFIC TERMS:\n${projectSpecificTerms}` : ''}`;

  const getFinalProjectId = () => projectId || internalProjectId || existingBid?.project_id;
  const getFinalClientId = () => clientId || internalClientId || existingBid?.client_id;

  // "How would you like to pay?" — the client's answer from the project
  // brief. Comes with the marketplace context; when the wizard is opened
  // from an existing quotation (no context) it is looked up here so the
  // stage rule and the Payment Stages prompt behave the same everywhere.
  const [fetchedFundingPreference, setFetchedFundingPreference] = useState(null);
  const clientFundingPreference = activeProjectContext?.fundingPreference || fetchedFundingPreference;
  useEffect(() => {
    const pid = getFinalProjectId();
    if (!open || !pid || activeProjectContext?.fundingPreference) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getApiClient().get(`/projects/${pid}/funding-preference`);
        if (!cancelled) setFetchedFundingPreference(res.data?.data?.fundingPreference || null);
      } catch (_) { /* no preference known */ }
    })();
    return () => { cancelled = true; };
  }, [open, projectId, internalProjectId, existingBid?.project_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Merge itemized BOQ lines into the stored cost_breakdown jsonb under a
  // reserved `lineItems` key. Old bids without it are unaffected.
  const buildCostBreakdownPayload = () => {
    const cleaned = Object.fromEntries(
      Object.entries(costLineItems)
        .map(([key, items]) => [key, (items || []).filter((item) => item.description?.trim())])
        .filter(([, items]) => items.length > 0)
    );
    return Object.keys(cleaned).length > 0
      ? { ...costBreakdown, lineItems: cleaned }
      : { ...costBreakdown };
  };

  // Stages that get stored/submitted. Empty is fine — the breakdown is then
  // agreed with the client after acceptance and created as project stages.
  const buildPaymentSchedulePayload = () => paymentStages
    .filter((stage) => stage && String(stage.name || '').trim() && (parseFloat(stage.amount) || 0) > 0)
    .map((stage) => ({
      name: stage.name.trim(),
      description: String(stage.description || '').trim(),
      amount: Number((parseFloat(stage.amount) || 0).toFixed(2))
    }));

  const setCategoryItems = (key, items) => {
    setCostLineItems((prev) => {
      const next = { ...prev };
      if (items === null) {
        delete next[key];
      } else {
        next[key] = items;
      }
      return next;
    });
    if (items !== null) {
      setCostBreakdown((prev) => ({ ...prev, [key]: Number(categoryItemsTotal(items).toFixed(2)) }));
    }
  };

  const buildBidPayload = () => {
    const selectedInclusions = getSelectedItemTexts(inclusions);
    const selectedExclusions = getSelectedItemTexts(exclusions);

    return {
      projectId: getFinalProjectId(),
      clientId: getFinalClientId(),
      executiveSummary,
      projectUnderstanding,
      scopeOfWork,
      inclusions: selectedInclusions,
      exclusions: selectedExclusions,
      assumptions,
      totalCost,
      currency,
      costBreakdown: buildCostBreakdownPayload(),
      paymentSchedule: buildPaymentSchedulePayload(),
      depositAmount,
      estimatedDuration: parseInt(estimatedDuration, 10) || null,
      durationUnit: ['days', 'weeks', 'months'].includes(durationUnit) ? durationUnit : 'weeks',
      estimatedStartDate: estimatedStartDate || null,
      termsAndConditions: fullTerms,
      warrantyPeriod,
      validUntil: validUntil || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      aiGenerated,
      aiPrompt: `${projectType}: ${projectDescription}\n${buildBidPreferenceContext()}\n${buildReadinessContext()}`,
      aiModel: 'gpt-4',
      attachments
    };
  };

  // UI state
  const [aiGenerated, setAiGenerated] = useState(false);
  const canCreatePricedBid = pricingEvidence.siteVisitCompleted || (
    pricingEvidence.photosReviewed &&
    (
      pricingEvidence.measurementsConfirmed ||
      pricingEvidence.materialSpecsConfirmed ||
      hasOwnerSuppliedEvidence
    )
  );
  const finalBidIntentLabel = 'Verified priced quotation';

  const steps = [
    'Project Readiness',
    'Summary & Scope',
    'Inclusions & Attachments',
    'Costs & BOQ',
    'Timeline & Terms'
  ];

  // Load available projects when dialog opens without projectId
  useEffect(() => {
    if (open && !projectId) {
      loadAvailableProjects();
    }
  }, [open, projectId]);

  // Populate project details from projectContext when dialog opens
  useEffect(() => {
    if (open && projectContext) {
      // Never fall back to the listing title — a full project name is not a
      // project type. Workflow values ('full', 'quick', 'other') are ignored
      // too: better an empty field the contractor fills than a meaningless one.
      const rawType = String(projectContext.type || projectContext.projectType || '').toLowerCase();
      const contextType = rawType && !['full', 'quick', 'other'].includes(rawType)
        ? PROJECT_TYPE_DISPLAY[rawType] ||
          rawType.replaceAll('_', ' ').replace(/^\w/, (c) => c.toUpperCase())
        : '';
      if (contextType && !projectType) {
        setProjectType(contextType);
      }
      if (projectContext.description && !projectDescription) {
        setProjectDescription(projectContext.description);
      }
    }
  }, [open, projectContext]);

  useEffect(() => {
    if (!open) return;
    const finalProjectId = getFinalProjectId();
    if (!finalProjectId) return;

    let isMounted = true;
    const loadProjectEvidence = async () => {
      try {
        setProjectEvidence((prev) => ({ ...prev, loading: true, error: '' }));
        const apiClient = getApiClient();
        const response = await apiClient.get(`/projects/${finalProjectId}/files`);
        const files = response.data?.files || [];
        const fileList = Array.isArray(files) ? files : [];
        const photos = fileList.filter((file) =>
          file.category === 'photo' ||
          file.fileType?.startsWith?.('image/') ||
          /\.(jpg|jpeg|png|gif|webp)$/i.test(file.fileName || file.url || '')
        );
        const documents = fileList.filter((file) =>
          file.category === 'document' ||
          file.fileType === 'application/pdf' ||
          file.fileType?.includes?.('document') ||
          /\.(pdf|doc|docx|xls|xlsx|csv)$/i.test(file.fileName || file.url || '')
        );

        if (isMounted) {
          setProjectEvidence({
            loading: false,
            photos,
            documents,
            files: fileList,
            error: ''
          });
        }
      } catch (error) {
        if (isMounted) {
          setProjectEvidence({
            loading: false,
            photos: [],
            documents: [],
            files: [],
            error: error.response?.data?.error || 'Could not load owner-supplied project files.'
          });
        }
      }
    };

    loadProjectEvidence();

    return () => {
      isMounted = false;
    };
  }, [open, projectId, internalProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    if (siteVisitCompleted && !pricingEvidence.siteVisitCompleted) {
      setPricingEvidence((prev) => ({ ...prev, siteVisitCompleted: true }));
    }
  }, [open, siteVisitCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  // Populate all fields when editing existing bid
  useEffect(() => {
    if (open && existingBid) {
      setServerDraftId(existingBid.id || null);
      // Step 1: Project Details
      setProjectType(existingBid.project_name || '');
      setProjectDescription(existingBid.project_description || '');

      // Step 2: Executive Summary
      setExecutiveSummary(existingBid.executive_summary || '');
      setProjectUnderstanding(existingBid.project_understanding || '');

      // Step 3: Scope of Work
      setScopeOfWork(existingBid.scope_of_work || '');

      // Step 4: Inclusions & Attachments
      const ensureArray = (data) => {
        if (!data) return [];
        if (Array.isArray(data)) return data;
        if (typeof data === 'string') {
          try {
            const parsed = JSON.parse(data);
            return Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            return [];
          }
        }
        return [];
      };

      setInclusions(normalizeSelectableItems(ensureArray(existingBid.inclusions)));
      setExclusions(normalizeSelectableItems(ensureArray(existingBid.exclusions)));
      setAssumptions(ensureArray(existingBid.assumptions));
      setAttachments(ensureArray(existingBid.supporting_documents));

      // Step 5: Cost Breakdown
      setTotalCost(parseFloat(existingBid.total_cost) || 0);
      setCurrency(existingBid.currency || 'USD');

      if (existingBid.cost_breakdown) {
        let breakdown = existingBid.cost_breakdown;
        if (typeof breakdown === 'string') {
          try {
            breakdown = JSON.parse(breakdown);
          } catch (e) {
            breakdown = {};
          }
        }
        if (breakdown && typeof breakdown === 'object') {
          setCostBreakdown(COST_BREAKDOWN_FIELDS.reduce((acc, field) => {
            acc[field.key] = parseFloat(breakdown[field.key] || 0);
            return acc;
          }, {}));
          setCostLineItems(
            breakdown.lineItems && typeof breakdown.lineItems === 'object' && !Array.isArray(breakdown.lineItems)
              ? breakdown.lineItems
              : {}
          );
        }
      }

      // Step 6: Timeline
      setEstimatedDuration(existingBid.estimated_duration || '');
      setDurationUnit(existingBid.duration_unit || 'weeks');
      {
        let schedule = existingBid.payment_schedule;
        if (typeof schedule === 'string') {
          try { schedule = JSON.parse(schedule); } catch (e) { schedule = []; }
        }
        setPaymentStages(Array.isArray(schedule) ? schedule.map((stage) => ({
          name: stage.name || '',
          description: stage.description || '',
          amount: stage.amount ?? ''
        })) : []);
      }
      setEstimatedStartDate(existingBid.estimated_start_date || '');

      // Step 7: Terms
      setDepositAmount(parseFloat(existingBid.deposit_amount) || 0);
      setWarrantyPeriod(existingBid.warranty_period || '12 months');
      setValidUntil(existingBid.valid_until || '');

      // Extract project-specific terms if present
      if (existingBid.terms_and_conditions) {
        const terms = existingBid.terms_and_conditions;
        if (terms.includes('PROJECT-SPECIFIC TERMS:')) {
          const parts = terms.split('PROJECT-SPECIFIC TERMS:');
          if (parts[1]) {
            setProjectSpecificTerms(parts[1].trim());
          }
        }
      }

      setAiGenerated(existingBid.ai_generated || false);
    }
  }, [open, existingBid]);

  // Load available projects for bidding
  const loadAvailableProjects = async () => {
    setLoadingProjects(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.get('/projects');
      const projects = response.data?.data?.projects || response.data?.projects || [];
      
      // Filter for projects open for bids
      const openProjects = projects.filter(p => 
        p.status === 'open_for_bids' || p.status === 'open'
      );
      
      setAvailableProjects(openProjects);
    } catch (error) {
      console.error('Failed to load projects:', error);
      setAvailableProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Handle project selection from dropdown
  const handleProjectSelect = (selectedProject) => {
    if (selectedProject) {
      setSelectedProjectOption(selectedProject);
      setProjectType(selectedProject.projectType || selectedProject.project_type || selectedProject.category || selectedProject.type || selectedProject.title || selectedProject.name || '');
      setInternalProjectId(selectedProject.id);
      setInternalClientId(selectedProject.owner_id || selectedProject.client_id);
      setInternalProjectContext({
        name: selectedProject.title || selectedProject.name,
        type: selectedProject.projectType || selectedProject.project_type || selectedProject.category || selectedProject.type,
        location: selectedProject.city || selectedProject.location,
        description: selectedProject.description,
        budget: selectedProject.total_budget || selectedProject.budget || selectedProject.estimated_budget,
        currency: selectedProject.currency || 'USD',
        timeline: selectedProject.timeline,
        proposalDeadline: selectedProject.proposal_deadline
      });
      // Auto-populate description if available
      if (selectedProject.description && !projectDescription) {
        setProjectDescription(selectedProject.description);
      }
    } else {
      setSelectedProjectOption(null);
      setProjectType('');
      setInternalProjectId(null);
      setInternalClientId(null);
      setInternalProjectContext({});
    }
  };

  // Keep an in-browser recovery draft only. This must not create a server bid.
  useEffect(() => {
    if (projectType && open) {
      const timer = setTimeout(() => {
        saveDraft();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [projectType, projectDescription, bidIntent, pricingEvidence, bidApproach, materialsResponsibility, siteVisitRequirement, paymentStructure, bidNotes, executiveSummary, scopeOfWork, inclusions, exclusions, costBreakdown, costLineItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save a recoverable browser draft. Server drafts are created only by Save Draft.
  const saveDraft = () => {
    const draftProjectId = projectId || internalProjectId;
    if (!draftProjectId) return; // Don't save if no project selected
    
    const draft = {
      projectType,
      projectDescription,
      activeStep,
      executiveSummary,
      projectUnderstanding,
      scopeOfWork,
      inclusions,
      exclusions,
      assumptions,
      costBreakdown,
      totalCost,
      bidIntent,
      pricingEvidence,
      bidApproach,
      materialsResponsibility,
      siteVisitRequirement,
      paymentStructure,
      bidNotes,
      estimatedDuration,
      durationUnit,
      estimatedStartDate,
      depositAmount,
      depositType,
      depositValue,
      projectSpecificTerms,
      warrantyPeriod,
      validUntil,
      costLineItems,
      paymentStages,
      stepsVersion: 5,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(`bid_draft_${draftProjectId}`, JSON.stringify(draft));
    setSavedDraft(true);
    setTimeout(() => setSavedDraft(false), 2000);
  };

  const saveServerDraft = async ({ closeAfterSave = false } = {}) => {
    const finalProjectId = getFinalProjectId();

    saveDraft();

    if (!finalProjectId) {
      showSnackbar('Choose a project before saving this quotation draft.', 'warning');
      return false;
    }

    setLoading(true);
    try {
      const apiClient = getApiClient();
      const bidData = buildBidPayload();
      const draftId = serverDraftId || existingBid?.id;
      const response = draftId
        ? await apiClient.put(`/bids/${draftId}`, bidData)
        : await apiClient.post('/bids', bidData);

      const savedBid = response.data.data;
      setServerDraftId(savedBid.id);
      showSnackbar('Draft saved to your My Requests & Quotes tab.', 'success');

      if (onBidCreated) {
        onBidCreated(savedBid);
      }

      if (closeAfterSave) {
        setShowPreview(false);
        onClose();
      }

      return true;
    } catch (error) {
      console.error('Error saving bid draft:', error);
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to save draft. Your local draft is still saved in this browser.';
      showSnackbar(message, 'error');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Load draft from localStorage
  useEffect(() => {
    const draftProjectId = projectId || internalProjectId;
    if (open && draftProjectId) {
      const savedDraft = localStorage.getItem(`bid_draft_${draftProjectId}`);
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        // Restore draft data
        setProjectType(draft.projectType || '');
        setProjectDescription(draft.projectDescription || '');
        setExecutiveSummary(draft.executiveSummary || '');
        setProjectUnderstanding(draft.projectUnderstanding || '');
        setScopeOfWork(draft.scopeOfWork || '');
        setInclusions(normalizeSelectableItems(draft.inclusions || []));
        setExclusions(normalizeSelectableItems(draft.exclusions || []));
        setAssumptions(draft.assumptions || []);
        const { total: _draftTotal, lineItems: _draftItems, ...draftCosts } = draft.costBreakdown || {};
        setCostBreakdown({ ...getEmptyCostBreakdown(), ...draftCosts });
        setCostLineItems(draft.costLineItems || _draftItems || {});
        setPaymentStages(Array.isArray(draft.paymentStages) ? draft.paymentStages : []);
        setBidIntent('priced_bid');
        setPricingEvidence({ ...getEmptyPricingEvidence(), ...(draft.pricingEvidence || {}) });
        setBidApproach(draft.bidApproach && draft.bidApproach !== 'assessment_first' ? draft.bidApproach : 'full_service');
        setMaterialsResponsibility(draft.materialsResponsibility || 'contractor_supplied');
        setSiteVisitRequirement(draft.siteVisitRequirement || 'recommended_before_start');
        setPaymentStructure(draft.paymentStructure || 'milestone_based');
        setBidNotes(draft.bidNotes || '');
        setEstimatedDuration(draft.estimatedDuration || '');
        setDurationUnit(['days', 'weeks', 'months'].includes(draft.durationUnit) ? draft.durationUnit : 'weeks');
        setEstimatedStartDate(draft.estimatedStartDate || '');
        setDepositAmount(parseFloat(draft.depositAmount) || 0);
        setDepositType(draft.depositType || 'amount');
        setDepositValue(draft.depositValue || '');
        setWarrantyPeriod(draft.warrantyPeriod || '12 months');
        setValidUntil(draft.validUntil || '');

        // Handle migration from old termsAndConditions to projectSpecificTerms
        if (draft.projectSpecificTerms !== undefined) {
          setProjectSpecificTerms(draft.projectSpecificTerms);
        } else if (draft.termsAndConditions) {
          setProjectSpecificTerms(draft.termsAndConditions.replace(FIXED_PLATFORM_TERMS, '').replace('PROJECT-SPECIFIC TERMS:', '').trim());
        }

        // Map old 7/8-step drafts onto the merged 5-step flow:
        // 0→0 (readiness), 1-2→1 (summary+scope), 3→2 (inclusions),
        // 4→3 (cost), 5+→4 (timeline+terms).
        if (draft.activeStep !== undefined) {
          const OLD_STEP_MAP = [0, 1, 1, 2, 3, 4, 4, 4];
          const savedStep = Number(draft.activeStep) || 0;
          setActiveStep(savedStep <= 4 && draft.stepsVersion === 5
            ? savedStep
            : OLD_STEP_MAP[Math.min(Math.max(savedStep, 0), OLD_STEP_MAP.length - 1)]);
        }
      }
    }
  }, [open, projectId]);

  // ============================================================================
  // AI GENERATION FUNCTIONS
  // ============================================================================

  /**
   * Generate complete bid with AI
   */
  const buildAiGeneratePayload = () => {
    const finalProjectContext = projectContext.name ? projectContext : internalProjectContext;
    const milestoneCtx = getMilestoneContext();
    const bidPreferenceContext = buildBidPreferenceContext();
    const readinessContext = buildReadinessContext();
    const intentInstruction = 'BID INTENT: Create a verified fixed-price professional quotation. Use pricing only where the contractor has confirmed the information needed to price responsibly.';
    return {
      projectType,
      projectDescription: [
        projectDescription,
        milestoneCtx ? `IMPORTANT: This bid is specifically for the "${milestoneCtx}" milestone/task only. Stay within this scope.` : '',
        intentInstruction,
        readinessContext,
        bidPreferenceContext
      ].filter(Boolean).join('\n\n'),
      projectDetails: {
        projectName: finalProjectContext.name || projectType,
        location: finalProjectContext.location,
        budget: finalProjectContext.budget || null,
        currency: finalProjectContext.currency || 'USD',
        proposalDeadline: finalProjectContext.proposalDeadline || null,
        clientRequirements: finalProjectContext.requirements,
        bidApproach,
        materialsResponsibility,
        siteVisitRequirement,
        paymentStructure,
        contractorNotes: bidNotes,
        bidIntent,
        pricingEvidence,
        milestoneName: finalProjectContext.milestoneName || null,
        milestoneDescription: finalProjectContext.milestoneDescription || null,
      },
    };
  };

  /**
   * Draft ONLY the three narrative sections (executive summary, project
   * understanding, scope of work) from the project context — AI already
   * knows the project, so the contractor shouldn't have to type seed text.
   * Costs, timeline, and terms are left untouched.
   */
  const handleDraftNarrativeWithAI = async () => {
    const hasExisting = executiveSummary.trim() || projectUnderstanding.trim() || scopeOfWork.trim();
    if (hasExisting && !window.confirm('Replace the current text in all three sections with a fresh AI draft?')) {
      return;
    }
    setGenerating(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.post('/bids/ai/generate', buildAiGeneratePayload());
      const generated = response.data.data;
      setExecutiveSummary(toSectionText(generated.executiveSummary));
      setProjectUnderstanding(toSectionText(generated.projectUnderstanding));
      setScopeOfWork(toSectionText(generated.scopeOfWork));
      clearFieldError('scopeOfWork');
      setAiGenerated(true);
      showSnackbar('✨ All three sections drafted — read every line and edit before continuing.', 'success');
    } catch (error) {
      console.error('Error drafting narrative sections:', error);
      showSnackbar(error.response?.data?.message || error.response?.data?.error || 'Failed to draft the sections. Please try again.', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateWithAI = async () => {
    if (!projectType.trim() || !projectDescription.trim()) {
      showSnackbar('Please enter project type and description first', 'warning');
      return;
    }

    if (!canCreatePricedBid) {
      showSnackbar(
        hasOwnerSuppliedEvidence
          ? 'Owner supplied photos/documents. Review them and check the relevant readiness boxes before generating the quotation.'
          : 'A verified professional quotation needs a site visit or photos, measurements, and material specs first. Use Request Assessment from the project card if details are missing.',
        'warning'
      );
      return;
    }

    setGenerating(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.post('/bids/ai/generate', buildAiGeneratePayload());

      const generated = response.data.data;

      // Populate all fields with AI-generated content
      setExecutiveSummary(toSectionText(generated.executiveSummary));
      setProjectUnderstanding(toSectionText(generated.projectUnderstanding));
      setScopeOfWork(toSectionText(generated.scopeOfWork));
      setInclusions(normalizeSelectableItems(generated.inclusions || []));
      setExclusions(normalizeSelectableItems(generated.exclusions || []));
      setAssumptions(generated.assumptions || []);
      // Deliberately NOT applying generated.costBreakdown — AI must never
      // invent prices. The contractor enters every amount themselves (or
      // builds a BOQ) in the Cost Breakdown step.
      setEstimatedDuration(generated.estimatedDuration || '');
      setDurationUnit(generated.durationUnit || 'weeks');
      
      // Set project-specific terms from AI (ensure string — model may return array/object)
      const terms = generated.termsAndConditions;
      setProjectSpecificTerms(typeof terms === 'string' ? terms : Array.isArray(terms) ? terms.join('\n') : '');

      setWarrantyPeriod(generated.warrantyPeriod || '12 months');

      setAiGenerated(true);

      // Move to next step
      setActiveStep(1);

      showSnackbar('✨ Professional quotation generated! Review and customize as needed.', 'success');
    } catch (error) {
      console.error('Error generating bid:', error);
      showSnackbar(error.response?.data?.message || error.response?.data?.error || 'Failed to generate quotation. Please try again or contact support.', 'error');
    } finally {
      setGenerating(false);
    }
  };

  /**
   * Build context string from project + milestone info for AI scoping
   */
  const buildBidContext = () => {
    const ctx = projectContext.name ? projectContext : internalProjectContext;
    const parts = [];
    if (ctx?.name) parts.push(`Project: "${ctx.name}"`);
    if (ctx?.type) parts.push(`Type: ${ctx.type}`);
    if (ctx?.location) parts.push(`Location: ${ctx.location}`);
    if (ctx?.budget) parts.push(`Client budget: ${ctx.currency || 'USD'} ${ctx.budget}`);
    if (ctx?.proposalDeadline) parts.push(`Bid deadline: ${ctx.proposalDeadline}`);
    parts.push(buildBidPreferenceContext());
    if (ctx?.milestoneName) parts.push(`Milestone: "${ctx.milestoneName}"`);
    if (ctx?.milestoneDescription) parts.push(`Task: ${ctx.milestoneDescription}`);
    if (projectDescription) parts.push(`Description: ${projectDescription}`);
    return parts.join(' | ');
  };

  /**
   * Get milestone context string if available
   */
  const getMilestoneContext = () => {
    const ctx = projectContext.name ? projectContext : internalProjectContext;
    if (ctx?.milestoneName) {
      return ctx.milestoneDescription
        ? `${ctx.milestoneName} — ${ctx.milestoneDescription}`
        : ctx.milestoneName;
    }
    return null;
  };

  const buildBidPreferenceContext = () => {
    const labels = {
      full_service: 'Full service: labor, coordination, and materials',
      labor_only: 'Labor only',
      milestone_only: 'Bid for a specific milestone or phase',
      contractor_supplied: 'Contractor supplies materials',
      client_supplied: 'Client supplies materials',
      mixed_materials: 'Mixed materials responsibility',
      required_before_quote: 'Site visit required before final quote',
      recommended_before_start: 'Site visit recommended before starting',
      not_required: 'Site visit not required',
      milestone_based: 'Milestone-based payment schedule',
      deposit_then_completion: 'Deposit then completion balance',
      monthly_progress: 'Monthly progress payments'
    };

    return [
      `Bid approach: ${labels[bidApproach] || bidApproach}`,
      `Materials: ${labels[materialsResponsibility] || materialsResponsibility}`,
      `Site visit: ${labels[siteVisitRequirement] || siteVisitRequirement}`,
      `Payment structure: ${labels[paymentStructure] || paymentStructure}`,
      bidNotes ? `Contractor notes: ${bidNotes}` : ''
    ].filter(Boolean).join(' | ');
  };

  const buildReadinessContext = () => {
    const confirmed = PRICING_EVIDENCE_ITEMS
      .filter((item) => pricingEvidence[item.key])
      .map((item) => item.label);
    const missing = PRICING_EVIDENCE_ITEMS
      .filter((item) => !pricingEvidence[item.key])
      .map((item) => item.label);

    return [
      `Bid intent: ${finalBidIntentLabel}`,
      hasOwnerSuppliedEvidence
        ? `Owner-supplied evidence available: ${ownerSuppliedPhotos} photo(s), ${ownerSuppliedDocs} document(s), marked details: ${ownerProvidedItems.join(', ') || 'none'}`
        : 'Owner-supplied evidence available: none detected',
      siteVisitDate
        ? `Site visit: ${siteVisitCompleted ? 'completed' : siteVisitHasPassed ? 'scheduled time has passed, awaiting confirmation' : 'scheduled'} for ${formatHarare(siteVisitDate)}`
        : 'Site visit: not scheduled',
      confirmed.length ? `Confirmed pricing basis: ${confirmed.join(', ')}` : 'Confirmed pricing basis: none yet',
      missing.length ? `Unconfirmed but reviewed by contractor before submission as needed: ${missing.join(', ')}` : ''
    ].filter(Boolean).join(' | ');
  };

  const formatProjectBudget = (amount, currencyCode = 'USD') => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      return 'Budget not listed';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(numericAmount);
  };

  const getBidSetupSummary = () => ([
    BID_SETUP_HELP.bidApproach[bidApproach],
    BID_SETUP_HELP.materialsResponsibility[materialsResponsibility],
    BID_SETUP_HELP.siteVisitRequirement[siteVisitRequirement],
    BID_SETUP_HELP.paymentStructure[paymentStructure]
  ].filter(Boolean));

  /**
   * Improve specific section with AI
   */
  const handleImproveSection = async (section, currentContent, setter) => {
    if (!currentContent.trim()) {
      showSnackbar('Please enter some content first', 'warning');
      return;
    }

    setLoading(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.post('/bids/ai/improve-section', {
        section,
        currentContent,
        improvementRequest: buildBidContext(),
        milestoneContext: getMilestoneContext(),
      });

      setter(response.data.data.improvedContent);
      showSnackbar('✨ Content improved!', 'success');
    } catch (error) {
      console.error('Error improving section:', error);
      showSnackbar('Failed to improve content. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // NOTE: there is intentionally no AI cost generation — prices are the
  // contractor's own numbers, never invented by a model.

  /**
   * Generate inclusions and exclusions with AI
   */
  const handleGenerateInclusionsExclusions = async () => {
    if (!scopeOfWork.trim()) {
      showSnackbar('Please enter scope of work first', 'warning');
      return;
    }

    setLoading(true);
    try {
      const apiClient = getApiClient();
      const response = await apiClient.post('/bids/ai/inclusions-exclusions', {
        projectType,
        scopeOfWork,
      });

      const data = response.data.data;
      setInclusions(normalizeSelectableItems(data.inclusions || []));
      setExclusions(normalizeSelectableItems(data.exclusions || []));
      setAssumptions(data.assumptions || []);

      showSnackbar('✨ Inclusions, exclusions, and assumptions generated!', 'success');
    } catch (error) {
      console.error('Error generating lists:', error);
      showSnackbar('Failed to generate lists. Please enter manually.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  // Add inclusion
  const handleAddInclusion = () => {
    if (newInclusion.trim()) {
      setInclusions([...normalizeSelectableItems(inclusions), {
        text: newInclusion.trim(),
        selected: true,
        custom: true
      }]);
      setNewInclusion('');
    }
  };

  // Add exclusion
  const handleAddExclusion = () => {
    if (newExclusion.trim()) {
      setExclusions([...normalizeSelectableItems(exclusions), {
        text: newExclusion.trim(),
        selected: true,
        custom: true
      }]);
      setNewExclusion('');
    }
  };

  const toggleSelectableItem = (setter, items, index) => {
    setter(normalizeSelectableItems(items).map((item, itemIndex) => (
      itemIndex === index ? { ...item, selected: !item.selected } : item
    )));
  };

  const removeSelectableItem = (setter, items, index) => {
    setter(normalizeSelectableItems(items).filter((_, itemIndex) => itemIndex !== index));
  };

  // Add file attachment (Mock)
  const handleAddAttachment = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setAttachments([...attachments, { name: file.name, size: file.size, type: file.type }]);
      showSnackbar('File attached successfully', 'success');
    }
  };

  // Calculate total cost from breakdown (exclude any stored 'total' key to avoid double-counting)
  useEffect(() => {
    const total = Object.entries(costBreakdown)
      .filter(([key]) => key !== 'total')
      .reduce((sum, [, val]) => sum + (parseFloat(val) || 0), 0);
    setTotalCost(total);
  }, [costBreakdown]);

  // Update cost breakdown field
  const updateCostBreakdown = (field, value) => {
    setCostBreakdown(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  };

  // Keep categories carrying money or line items visible (drafts, edit mode,
  // AI generation), on top of the always-shown core set.
  useEffect(() => {
    setVisibleCostKeys((prev) => {
      const withData = COST_BREAKDOWN_FIELDS
        .map((field) => field.key)
        .filter((key) => (parseFloat(costBreakdown[key]) || 0) > 0 || (costLineItems[key] || []).length > 0);
      const merged = [...new Set([...CORE_COST_KEYS, ...prev, ...withData])];
      return merged.length === prev.length && merged.every((key, index) => key === prev[index]) ? prev : merged;
    });
  }, [costBreakdown, costLineItems]);

  // Update Deposit Amount
  useEffect(() => {
    const val = parseFloat(depositValue) || 0;
    if (depositType === 'amount') {
      setDepositAmount(val);
    } else {
      setDepositAmount((totalCost * val) / 100);
    }
  }, [depositValue, depositType, totalCost]);

  // ============================================================================
  // NAVIGATION
  // ============================================================================

  const handleNext = () => {
    // Clear previous errors
    clearAllFieldErrors();

    // Validation for each step
    if (activeStep === 0) {
      // Step 0: Project Details
      if (!projectId && !selectedProjectOption) {
        showSnackbar('Please select a project to quote on', 'warning');
        return;
      }
      if (!projectType.trim()) {
        setFieldError('projectType', 'Project type is required');
        showSnackbar('Please fill in all required fields', 'warning');
        return;
      }
      if (!projectDescription.trim()) {
        setFieldError('projectDescription', 'Project description is required');
        showSnackbar('Please fill in all required fields', 'warning');
        return;
      }
      if (projectDescription.length < 20) {
        setFieldError('projectDescription', 'Description must be at least 20 characters');
        showSnackbar('Description too short (minimum 20 characters)', 'warning');
        return;
      }
      if (!canCreatePricedBid) {
        showSnackbar(
          hasOwnerSuppliedEvidence
            ? 'Owner supplied photos/documents. Review them and check the relevant readiness boxes before continuing.'
            : 'This is not ready for a verified quotation yet. Request an assessment/details from the project card first, or confirm the pricing basis here.',
          'warning'
        );
        return;
      }
    }

    if (activeStep === 1) {
      // Step 1: Summary & Scope (summary optional, scope required)
      if (!scopeOfWork.trim()) {
        setFieldError('scopeOfWork', 'Scope of work is required');
        showSnackbar('Please enter scope of work', 'warning');
        return;
      }
      if (scopeOfWork.length < 50) {
        setFieldError('scopeOfWork', 'Scope must be at least 50 characters');
        showSnackbar('Scope of work too short (minimum 50 characters)', 'warning');
        return;
      }
    }

    if (activeStep === 3) {
      // Step 3: Cost Breakdown
      if (totalCost <= 0) {
        setFieldError('totalCost', 'Total cost must be greater than 0');
        showSnackbar('Please enter cost breakdown', 'warning');
        return;
      }
    }

    if (activeStep === 4) {
      // Step 4: Timeline, Deposit & Terms
      if (!estimatedDuration || estimatedDuration <= 0) {
        setFieldError('estimatedDuration', 'Duration is required');
        showSnackbar('Please enter estimated duration', 'warning');
        return;
      }
      // Start date is optional — a contractor often can't commit to one
      // before the bid is even accepted.
      if (!validUntil) {
        setFieldError('validUntil', 'Quotation valid-until date is required');
        showSnackbar('Please enter a "Quotation Valid Until" date', 'warning');
        return;
      }
      // Stage plan rule (mirrored on the server at submit): over the
      // threshold a quotation needs at least two stages, adding up to the
      // total, with a modest first stage — so the client is asked to start
      // with a number they can act on, not the lump sum.
      const stageProblem = stagePlanProblem(paymentStages, totalCost, currency, clientFundingPreference === 'stages');
      if (stageProblem) {
        showSnackbar(stageProblem, 'warning');
        return;
      }
    }

    if (activeStep === steps.length - 1) {
       // Final Step (Deposit & Terms) - Open Preview
       setShowPreview(true);
    } else {
       setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleClose = () => {
    setCloseDialogOpen(true);
  };

  const handleCloseConfirm = () => {
    setCloseDialogOpen(false);
    saveDraft();
    handleResetForm();
    onClose();
  };

  const handleRequestAssessment = () => {
    saveDraft();
    onClose();
    if (onRequestAssessment) {
      onRequestAssessment(projectContext.name ? projectContext : internalProjectContext);
      return;
    }
    navigate('/contractor/marketplace', {
      state: internalProjectId ? { focusJobId: internalProjectId } : undefined
    });
  };

  // ============================================================================
  // SUBMIT BID
  // ============================================================================

  // Persist the bid (create or update) and return the saved record.
  const saveBidToServer = async () => {
    const apiClient = getApiClient();
    const bidData = buildBidPayload();
    const draftId = serverDraftId || existingBid?.id;
    const response = draftId
      ? await apiClient.put(`/bids/${draftId}`, bidData)
      : await apiClient.post('/bids', bidData);
    const savedBid = response.data.data;
    setServerDraftId(savedBid.id);
    return savedBid;
  };

  const finishAfterSave = (savedBid) => {
    const draftKey = projectId || internalProjectId;
    if (draftKey) {
      localStorage.removeItem(`bid_draft_${draftKey}`);
    }
    if (onBidCreated) {
      onBidCreated(savedBid);
    }
    setShowPreview(false);
    handleResetForm();
    onClose();
  };

  const handleSubmitBid = async () => {
    const finalProjectId = getFinalProjectId();
    if (!finalProjectId) {
      showSnackbar('Missing project details. Please reopen the project from the marketplace and try again.', 'error');
      return;
    }

    setLoading(true);
    try {
      const savedBid = await saveBidToServer();
      showSnackbar('Quotation saved as a draft — it has NOT been sent. Open your Quotations page to submit it when ready.', 'success');
      finishAfterSave(savedBid);
    } catch (error) {
      console.error('Error creating bid:', error);
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to create the quotation. Please try again.';
      showSnackbar(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Save, then send straight to the client (notified in-app + email).
  const handleSubmitBidToClient = async () => {
    const finalProjectId = getFinalProjectId();
    if (!finalProjectId) {
      showSnackbar('Missing project details. Please reopen the project from the marketplace and try again.', 'error');
      return;
    }

    setLoading(true);
    try {
      const savedBid = await saveBidToServer();
      const apiClient = getApiClient();
      await apiClient.post(`/bids/${savedBid.id}/submit`);
      showSnackbar('✅ Quotation submitted — the client has been notified in-app and by email.', 'success');
      finishAfterSave(savedBid);
    } catch (error) {
      console.error('Error submitting bid:', error);
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to submit the quotation. It may be saved as a draft — check your Quotations page.';
      showSnackbar(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetForm = () => {
    setActiveStep(0);
    setUploadMode(false);
    setUploadFile(null);
    setUploadStoredFile(null);
    setUploadExtraction(null);
    setUploadPhase('idle');
    setUploadTotal('');
    setUploadCurrency('USD');
    setUploadValidUntil('');
    setUploadNotes('');
    setUploadConfirmed(false);
    setProjectType('');
    setProjectDescription('');
    setPaymentStages([]);
    setCostLineItems({});
    setExecutiveSummary('');
    setProjectUnderstanding('');
    setScopeOfWork('');
    setSelectedProjectOption(null);
    setInternalProjectId(null);
    setInternalClientId(null);
    setInternalProjectContext({});
    setServerDraftId(null);
    setPricingDecision(null);
    setBidIntent('priced_bid');
    setPricingEvidence(getEmptyPricingEvidence());
    setBidApproach('full_service');
    setMaterialsResponsibility('contractor_supplied');
    setSiteVisitRequirement('recommended_before_start');
    setPaymentStructure('milestone_based');
    setBidNotes('');
    setInclusions([]);
    setExclusions([]);
    setAssumptions([]);
    setCostBreakdown(getEmptyCostBreakdown());
    setTotalCost(0);
    setEstimatedDuration('');
    setProjectSpecificTerms('');
    setAiGenerated(false);
    setAttachments([]);
  };

  // Handle edits from Preview Modal
  const handlePreviewEdit = (section, value) => {
    switch(section) {
      case 'executiveSummary': setExecutiveSummary(value); break;
      case 'scopeOfWork': setScopeOfWork(value); break;
      case 'termsAndConditions': 
        // If they edit the full terms in preview, we store the delta as projectSpecificTerms
        setProjectSpecificTerms(value.replace(FIXED_PLATFORM_TERMS, '').replace('PROJECT-SPECIFIC TERMS:', '').trim()); 
        break;
      default: break;
    }
  };

  // ============================================================================
  // RENDER STEP CONTENT
  // ============================================================================

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: return renderStep1ProjectDetails();
      case 1: return (
        <>
          <Paper
            variant="outlined"
            sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', bgcolor: 'primary.50' }}
          >
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Let AI write these sections for you
              </Typography>
              <Typography variant="body2" color="text.secondary">
                AI already knows this project from the details and evidence you confirmed. One click drafts the Executive Summary, Project Understanding, and Scope of Work — then read and edit them below.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
              disabled={generating}
              onClick={handleDraftNarrativeWithAI}
              sx={{ background: brand.gradient, '&:hover': { background: brand.gradientHover }, flexShrink: 0 }}
            >
              {generating
                ? 'Writing…'
                : (executiveSummary.trim() || projectUnderstanding.trim() || scopeOfWork.trim())
                  ? 'Rewrite with AI'
                  : 'Write these sections with AI'}
            </Button>
          </Paper>
          {(executiveSummary.trim() || projectUnderstanding.trim() || scopeOfWork.trim()) && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <strong>Read before you send.</strong> This document goes to the client under your name — check every line below and edit anything that doesn't match how you will actually deliver the job.
            </Alert>
          )}
          {renderStep2ExecutiveSummary()}
          <Divider sx={{ my: 3 }} />
          {renderStep3ScopeOfWork()}
        </>
      );
      case 2: return renderStep4InclusionsExclusions();
      case 3: return renderStep5CostBreakdown();
      case 4: return (
        <>
          {renderStep6Timeline()}
          <Divider sx={{ my: 3 }} />
          {renderPaymentStages()}
          <Divider sx={{ my: 3 }} />
          {renderStep7Terms()}
        </>
      );
      default: return null;
    }
  };

  // ============================================================================
  // UPLOAD YOUR OWN QUOTATION (fast path)
  // ============================================================================

  const uploadReady = !!uploadFile && Number(uploadTotal) > 0 && !!uploadValidUntil && uploadConfirmed
    && uploadPhase !== 'uploading' && uploadPhase !== 'extracting'
    && !!(getFinalProjectId() || selectedProjectOption);

  const resetUploadedDocument = () => {
    setUploadFile(null);
    setUploadStoredFile(null);
    setUploadExtraction(null);
    setUploadPhase('idle');
  };

  // Upload the document as soon as it's chosen, then AI-read it to prefill
  // empty fields. Best-effort: any failure falls back to manual entry.
  const handleUploadFileSelected = async (file) => {
    if (!file) return;
    setUploadFile(file);
    setUploadStoredFile(null);
    setUploadExtraction(null);
    setUploadPhase('uploading');
    const apiClient = getApiClient();

    let stored = null;
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await apiClient.post('/uploads/quotation-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      stored = uploadRes.data.data;
      setUploadStoredFile(stored);
    } catch (error) {
      // Upload itself failed (bad type, too large, network) — surface it and
      // let the contractor pick another file.
      const message = error.response?.data?.message || error.response?.data?.error || 'Upload failed — please try again.';
      showSnackbar(message, 'error');
      setUploadFile(null);
      setUploadPhase('idle');
      return;
    }

    const isReadable = stored.mimetype === 'application/pdf' || String(stored.mimetype || '').startsWith('image/');
    if (!isReadable) {
      setUploadPhase('ready');
      return;
    }

    try {
      setUploadPhase('extracting');
      const extractRes = await apiClient.post('/bids/quotation-extract', { key: stored.key });
      const result = extractRes.data?.data;
      if (result?.available && result.extracted) {
        const ex = result.extracted;
        setUploadExtraction(ex);
        // Cover-sheet prefill: only into fields the contractor hasn't typed in.
        const incl = (ex.inclusions && ex.inclusions.length) ? ex.inclusions : (ex.lineItems || []).map((li) => li.description).filter(Boolean).slice(0, 8);
        setUploadInclusions((prev) => (prev.trim() ? prev : incl.join('\n')));
        setUploadExclusions((prev) => (prev.trim() ? prev : (ex.exclusions || []).join('\n')));
        if (ex.durationValue) { setUploadDuration((prev) => prev || String(ex.durationValue)); if (ex.durationUnit) setUploadDurationUnit(ex.durationUnit); }
        if (ex.materialsSuppliedBy) setUploadMaterials(ex.materialsSuppliedBy === 'client' ? 'client_supplied' : 'contractor_supplied');
        if (ex.startCondition) setUploadStartCondition(ex.startCondition);
        setUploadTotal((prev) => (prev && Number(prev) > 0 ? prev : (ex.totalAmount != null ? String(ex.totalAmount) : prev)));
        if (ex.currency === 'USD' || ex.currency === 'ZWG') setUploadCurrency(ex.currency);
        if (ex.validUntil) setUploadValidUntil(ex.validUntil);
      }
    } catch (error) {
      // Extraction is a convenience — keep the uploaded file, fall back to manual
      console.warn('Quotation extraction failed:', error);
    }
    setUploadPhase('ready');
  };

  const handleUploadQuotationSubmit = async () => {
    const finalProjectId = getFinalProjectId();
    if (!finalProjectId) {
      showSnackbar('Select the project this quotation is for.', 'error');
      return;
    }
    const uploadStageProblem = stagePlanProblem(paymentStages, Number(uploadTotal) || 0, uploadCurrency, clientFundingPreference === 'stages');
    if (uploadStageProblem) {
      showSnackbar(uploadStageProblem, 'warning');
      return;
    }
    setLoading(true);
    try {
      const apiClient = getApiClient();

      let stored = uploadStoredFile;
      if (!stored) {
        const formData = new FormData();
        formData.append('file', uploadFile);
        const uploadRes = await apiClient.post('/uploads/quotation-document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        stored = uploadRes.data.data;
      }

      const bidRes = await apiClient.post('/bids', {
        projectId: finalProjectId,
        clientId: clientId || internalClientId || undefined,
        quoteSource: 'uploaded',
        totalCost: Number(uploadTotal),
        currency: uploadCurrency,
        validUntil: uploadValidUntil,
        paymentSchedule: buildPaymentSchedulePayload(),
        // Cover sheet — the same columns a built quotation fills, so the
        // client sees one summary card for every quotation.
        estimatedDuration: Number(uploadDuration) > 0 ? Number(uploadDuration) : null,
        durationUnit: uploadDurationUnit,
        inclusions: linesOf(uploadInclusions),
        exclusions: linesOf(uploadExclusions),
        assumptions: [
          uploadStartCondition.trim() ? `Start: ${uploadStartCondition.trim()}` : null,
          uploadMaterials === 'client_supplied' ? 'Materials are supplied by the client; this quotation covers labour and the items listed.' : 'Materials are supplied by the contractor and included in this quotation.',
        ].filter(Boolean),
        executiveSummary: uploadNotes.trim() || null,
        scopeOfWork: 'Full scope, pricing detail, and terms are in the attached quotation document.',
        // Extracted data is stored for records/analytics only — kept out of
        // lineItems so BOQ seeding never uses unconfirmed AI output.
        costBreakdown: uploadExtraction ? {
          extractedLineItems: uploadExtraction.lineItems || [],
          extraction: {
            model: uploadExtraction.model,
            confidence: uploadExtraction.confidence,
            totalAmount: uploadExtraction.totalAmount,
            currency: uploadExtraction.currency,
            validUntil: uploadExtraction.validUntil,
            extractedAt: uploadExtraction.extractedAt
          }
        } : {},
        attachments: [{
          type: 'quotation_document',
          key: stored.key,
          name: stored.originalname,
          size: stored.size,
          mimeType: stored.mimetype
        }]
      });
      const savedBid = bidRes.data.data;
      await apiClient.post(`/bids/${savedBid.id}/submit`);

      showSnackbar('✅ Quotation sent — the client has been notified and can accept it through Eqaya.', 'success');
      finishAfterSave(savedBid);
    } catch (error) {
      console.error('Upload quotation error:', error);
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to send the quotation. Please try again.';
      showSnackbar(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderUploadQuotation = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Send your own quotation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload the quotation you prepared on your own letterhead. Your client sees your document exactly as you made it — plus a secure Accept &amp; Pay button.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Quotations sent this way are eligible for the Eqaya project wallet, acceptance record, stage tracking, and dispute support.
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {!projectId && !internalProjectId && (
          <Autocomplete
            options={availableProjects}
            getOptionLabel={(option) => option.title || option.name || ''}
            loading={loadingProjects}
            value={selectedProjectOption}
            onChange={(event, newValue) => handleProjectSelect(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Which project is this quotation for? *"
                placeholder="Search for the project..."
                required
              />
            )}
            sx={{ width: '100%' }}
          />
        )}

        <Paper
          variant="outlined"
          sx={{ p: 3, textAlign: 'center', borderStyle: 'dashed', bgcolor: uploadFile ? 'success.50' : 'grey.50' }}
        >
          {uploadFile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              <InsertDriveFile color="success" />
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{uploadFile.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                ({(uploadFile.size / (1024 * 1024)).toFixed(1)} MB)
              </Typography>
              <Button size="small" onClick={resetUploadedDocument} disabled={uploadPhase === 'uploading' || uploadPhase === 'extracting'}>
                Change file
              </Button>
            </Box>
          ) : (
            <Button component="label" variant="contained" startIcon={<CloudUpload />}>
              Choose Quotation File
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,image/*"
                onChange={(e) => handleUploadFileSelected(e.target.files?.[0] || null)}
              />
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            PDF, Word, Excel, or a clear photo of your printed quotation (max 10MB)
          </Typography>
        </Paper>

        {(uploadPhase === 'uploading' || uploadPhase === 'extracting') && (
          <Alert severity="info" icon={<CircularProgress size={18} />}>
            {uploadPhase === 'uploading' ? 'Uploading your document…' : 'Reading your quotation to prefill the details…'}
          </Alert>
        )}

        {uploadPhase === 'ready' && uploadExtraction && uploadExtraction.totalAmount != null && (
          <Alert severity="success">
            <Typography variant="body2">
              We read your document: total <strong>{uploadExtraction.currency || uploadCurrency} {Number(uploadExtraction.totalAmount).toLocaleString()}</strong>
              {uploadExtraction.validUntil ? `, valid until ${uploadExtraction.validUntil}` : ''}
              {uploadExtraction.lineItems?.length ? `, ${uploadExtraction.lineItems.length} line item${uploadExtraction.lineItems.length === 1 ? '' : 's'}` : ''}.
              {' '}Check the fields below match your document — you are responsible for the confirmed total.
            </Typography>
          </Alert>
        )}

        {uploadPhase === 'ready' && uploadStoredFile && (!uploadExtraction || uploadExtraction.totalAmount == null) && (
          <Alert severity="info">
            We couldn't read a total from this document automatically — enter the details below yourself.
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select value={uploadCurrency} label="Currency" onChange={(e) => setUploadCurrency(e.target.value)}>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="ZWG">ZWG</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              type="number"
              label="Total amount"
              value={uploadTotal}
              onChange={(e) => setUploadTotal(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              type="date"
              label="Quotation valid until"
              value={uploadValidUntil}
              onChange={(e) => setUploadValidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {/* Same stage rule as the built quotation: an uploaded PDF over the
            threshold cannot be submitted without stages, so the form must
            ask for them here rather than fail at submit. */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          {renderPaymentStages(Number(uploadTotal) || 0, uploadCurrency)}
        </Paper>

        {/* Cover sheet: what the client sees next to the PDF. */}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Cover sheet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your PDF stays the document. These few lines are shown to the client next to it, so they can compare quotations at a glance, and they are recorded with the acceptance. {uploadExtraction ? 'We prefilled them from your PDF — check and edit.' : 'Fill them in from your PDF.'}
          </Typography>
          {(() => {
            const items = uploadExtraction?.lineItems || [];
            const unpriced = items.filter((li) => !(li.amount > 0) && !(li.unitPrice > 0)).length;
            if (items.length >= 3 && unpriced / items.length >= 0.5) {
              return (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Your PDF lists {unpriced} of {items.length} items without a price against them. Clients trust itemised quotations more and accept them faster. You can still send it as is.
                </Alert>
              );
            }
            return null;
          })()}
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Duration" value={uploadDuration} onChange={(e) => setUploadDuration(e.target.value)} inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Unit</InputLabel>
                <Select value={uploadDurationUnit} label="Unit" onChange={(e) => setUploadDurationUnit(e.target.value)}>
                  <MenuItem value="days">days</MenuItem>
                  <MenuItem value="weeks">weeks</MenuItem>
                  <MenuItem value="months">months</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Materials</InputLabel>
                <Select value={uploadMaterials} label="Materials" onChange={(e) => setUploadMaterials(e.target.value)}>
                  <MenuItem value="contractor_supplied">I supply materials (included in the total)</MenuItem>
                  <MenuItem value="client_supplied">Client supplies materials (labour only)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Work starts" value={uploadStartCondition} onChange={(e) => setUploadStartCondition(e.target.value)} helperText="When you can start, e.g. within 2 weeks of the first stage being funded" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth multiline minRows={4} label="Included (one per line)" value={uploadInclusions} onChange={(e) => setUploadInclusions(e.target.value)} placeholder={'Clearing of site\nSupply and install 1.8 m game fence\nDouble gate 4 m'} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth multiline minRows={4} label="Not included (one per line)" value={uploadExclusions} onChange={(e) => setUploadExclusions(e.target.value)} placeholder={'Council approvals\nTransport beyond 50 km\nElectrical work'} />
            </Grid>
          </Grid>
        </Paper>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Message to the client (optional)"
          value={uploadNotes}
          onChange={(e) => setUploadNotes(e.target.value)}
          placeholder="Anything you want to highlight about this quotation..."
        />

        <Alert severity="warning">
          <FormControlLabel
            control={
              <Checkbox
                checked={uploadConfirmed}
                onChange={(e) => setUploadConfirmed(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I confirm the total amount above matches my quotation document. This total is what my client accepts and what Eqaya payments are based on.
              </Typography>
            }
          />
        </Alert>
      </Box>
    </Box>
  );

  // ============================================================================
  // STEP 1: PROJECT DETAILS
  // ============================================================================

  const renderStep1ProjectDetails = () => (
    <Box>
      {!isEditMode && !pricingDecision && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Can you price this project confidently?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Start with the decision a real contractor makes first. If details are missing, request them before building a fixed-price quotation.
          </Typography>

          {(projectContext.name || internalProjectContext.name) && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Project Snapshot
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Project</Typography>
                  <Typography variant="body2">{(projectContext.name || internalProjectContext.name) || 'Not listed'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Location</Typography>
                  <Typography variant="body2">{(projectContext.location || internalProjectContext.location) || 'Not listed'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Client Budget</Typography>
                  <Typography variant="body2">
                    {formatProjectBudget(
                      projectContext.budget || internalProjectContext.budget,
                      projectContext.currency || internalProjectContext.currency || 'USD'
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Quotation Deadline</Typography>
                  <Typography variant="body2">{(projectContext.proposalDeadline || internalProjectContext.proposalDeadline) || 'Not listed'}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{ p: 2, height: '100%', borderColor: 'success.main', bgcolor: 'success.50' }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Yes, I have enough details
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Continue when you have a site visit, current photos, measurements, drawings, material choices, or another reliable pricing basis.
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AutoAwesome />}
                  onClick={() => setPricingDecision('ready')}
                  sx={{ background: brand.gradient }}
                >
                  Build Professional Quotation
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{ p: 2, height: '100%', borderColor: 'warning.main', bgcolor: 'warning.50' }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  No, I need more details
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Ask for plans, photos, measurements, material specs, a call, verifier report, BOQ, or a site visit before pricing.
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FactCheck />}
                  onClick={handleRequestAssessment}
                >
                  Request Details First
                </Button>
              </Paper>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2, mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 240 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Already have a quotation on your own letterhead?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload your PDF, confirm the total, and send it. Your client sees your document and can accept and pay securely through Eqaya.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => {
                if (!uploadValidUntil) {
                  setUploadValidUntil(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
                }
                setUploadMode(true);
              }}
              sx={{ flexShrink: 0 }}
            >
              Upload Your Own Quotation
            </Button>
          </Paper>
        </Box>
      )}

      {(isEditMode || pricingDecision) && (
      <>
      <Typography variant="h6" gutterBottom>
        What project are you bidding on?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Confirm whether you have enough information for a verified price. If not, use Request Assessment from the project card before building a quotation.
      </Typography>

      {(projectContext.name || internalProjectContext.name) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Project Snapshot
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Project</Typography>
              <Typography variant="body2">{(projectContext.name || internalProjectContext.name) || 'Not listed'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Location</Typography>
              <Typography variant="body2">{(projectContext.location || internalProjectContext.location) || 'Not listed'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Client Budget</Typography>
              <Typography variant="body2">
                {formatProjectBudget(
                  projectContext.budget || internalProjectContext.budget,
                  projectContext.currency || internalProjectContext.currency || 'USD'
                )}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Quotation Deadline</Typography>
              <Typography variant="body2">{(projectContext.proposalDeadline || internalProjectContext.proposalDeadline) || 'Not listed'}</Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {!projectId ? (
          <Autocomplete
            options={availableProjects}
            getOptionLabel={(option) => option.title || option.name || ''}
            loading={loadingProjects}
            value={selectedProjectOption}
            onChange={(event, newValue) => handleProjectSelect(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Project *"
                placeholder="Search for a project to quote on..."
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingProjects ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ width: '100%' }}
          />
        ) : (
          <TextField
            fullWidth
            label="Project Type *"
            value={projectType}
            onChange={(e) => setProjectType(e.target.value)}
            required
          />
        )}

        <TextField
          fullWidth
          multiline
          rows={6}
          label="Project Description *"
          value={projectDescription}
          onChange={(e) => {
            setProjectDescription(e.target.value);
            clearFieldError('projectDescription');
          }}
          placeholder="Describe the project in detail..."
          required
          error={!!fieldErrors.projectDescription}
          helperText={
            fieldErrors.projectDescription ||
            `${projectDescription.length} characters (minimum 20 recommended)`
          }
        />

        <Paper variant="outlined" sx={{ p: 2, bgcolor: canCreatePricedBid ? 'success.50' : 'warning.50' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Project Readiness
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A verified quotation needs enough pricing basis. Use the owner-supplied evidence below, then confirm what you have personally reviewed.
          </Typography>

          {projectEvidence.loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Checking owner-supplied photos and documents...
              </Typography>
            </Box>
          )}

          {!projectEvidence.loading && hasOwnerSuppliedEvidence && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, width: '100%' }}>
                  Owner supplied evidence for this project. Review it before continuing.
                </Typography>
                {ownerSuppliedPhotos > 0 && (
                  <Chip icon={<PhotoLibrary />} size="small" label={`${ownerSuppliedPhotos} photo${ownerSuppliedPhotos === 1 ? '' : 's'}`} />
                )}
                {ownerSuppliedDocs > 0 && (
                  <Chip icon={<InsertDriveFile />} size="small" label={`${ownerSuppliedDocs} document${ownerSuppliedDocs === 1 ? '' : 's'}`} />
                )}
                {ownerProvidedItems.length > 0 && (
                  <Chip size="small" label={`Owner marked ${ownerProvidedItems.length} detail${ownerProvidedItems.length === 1 ? '' : 's'} provided`} />
                )}
              </Box>
            </Alert>
          )}

          {siteVisitDate && !siteVisitCompleted && (
            <Alert severity={siteVisitHasPassed ? 'warning' : 'info'} icon={<EventAvailable />} sx={{ mb: 2 }}>
              {siteVisitHasPassed
                ? `The scheduled site visit time has passed (${formatHarare(siteVisitDate)}). It still needs confirmation before it should count as completed.`
                : `Site visit scheduled for ${formatHarare(siteVisitDate)}. You can draft from supplied evidence, but do not mark the site visit completed until it actually happens.`}
            </Alert>
          )}

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {PRICING_EVIDENCE_ITEMS.map((item) => {
              // The platform verified the site visit itself (geolocated flow,
              // proof photos, completion record) — show it as a fact, not a
              // self-attested checkbox.
              const isVerifiedVisit = item.key === 'siteVisitCompleted' && siteVisitCompleted;
              return (
              <Grid item xs={12} sm={6} key={item.key}>
                <Paper
                  variant="outlined"
                  sx={{ p: 1.25, height: '100%', bgcolor: isVerifiedVisit ? 'success.50' : 'background.paper', borderColor: isVerifiedVisit ? 'success.light' : undefined }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!pricingEvidence[item.key]}
                          disabled={isVerifiedVisit}
                          onChange={(event) => {
                            const nextEvidence = {
                              ...pricingEvidence,
                              [item.key]: event.target.checked
                            };
                            setPricingEvidence(nextEvidence);
                          }}
                        />
                      }
                      label={item.key === 'siteVisitCompleted' && siteVisitDate && !siteVisitCompleted
                        ? 'I have visited or inspected the site (not completed yet)'
                        : item.label}
                      sx={{ alignItems: 'flex-start', mr: 0 }}
                    />
                    {isVerifiedVisit && (
                      <Chip size="small" color="success" icon={<CheckCircle />} label="Verified" sx={{ mt: 0.75, flexShrink: 0 }} />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>
                    {isVerifiedVisit
                      ? `Site visit completed${siteVisitDate ? ` on ${new Date(siteVisitDate).toLocaleDateString()}` : ''}${assessmentVisitPhotoCount > 0 ? ` — ${assessmentVisitPhotoCount} photo${assessmentVisitPhotoCount === 1 ? '' : 's'} you took at the site are on record` : ''}.`
                      : item.key === 'photosReviewed' && ownerSuppliedPhotos > 0
                        ? `Owner supplied ${ownerSuppliedPhotos} current site photo${ownerSuppliedPhotos === 1 ? '' : 's'} for review.`
                        : item.key === 'measurementsConfirmed' && ownerSuppliedDocs > 0
                          ? `Owner supplied ${ownerSuppliedDocs} document${ownerSuppliedDocs === 1 ? '' : 's'} that may include drawings, measurements, specs, or reports.`
                          : item.helper}
                  </Typography>
                </Paper>
              </Grid>
              );
            })}
          </Grid>

          <Alert
            severity={canCreatePricedBid ? 'success' : hasOwnerSuppliedEvidence ? 'info' : 'warning'}
            action={!canCreatePricedBid && !hasOwnerSuppliedEvidence ? (
              <Button color="inherit" size="small" onClick={handleRequestAssessment}>
                Request details
              </Button>
            ) : undefined}
          >
            {canCreatePricedBid
              ? 'This project is ready for a verified priced quotation. Still review assumptions, exclusions, and materials before submission.'
              : hasOwnerSuppliedEvidence
                ? 'Owner supplied evidence is available. Review the photos/documents and check the relevant boxes above before continuing.'
                : 'This project is not ready for a verified quotation yet. Request plans, photos, measurements, material specs, a verifier report, or a site visit first.'}
          </Alert>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Bid Setup
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These choices help AI create a practical construction quotation instead of a generic document.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Quotation Approach</InputLabel>
                <Select
                  value={bidApproach}
                  label="Quotation Approach"
                  onChange={(e) => setBidApproach(e.target.value)}
                >
                  <MenuItem value="full_service">Full service</MenuItem>
                  <MenuItem value="labor_only">Labor only</MenuItem>
                  <MenuItem value="milestone_only">Specific milestone only</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {BID_SETUP_HELP.bidApproach[bidApproach]}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Materials</InputLabel>
                <Select
                  value={materialsResponsibility}
                  label="Materials"
                  onChange={(e) => setMaterialsResponsibility(e.target.value)}
                >
                  <MenuItem value="contractor_supplied">Contractor supplies materials</MenuItem>
                  <MenuItem value="client_supplied">Client supplies materials</MenuItem>
                  <MenuItem value="mixed_materials">Mixed materials responsibility</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {BID_SETUP_HELP.materialsResponsibility[materialsResponsibility]}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Site Visit</InputLabel>
                <Select
                  value={siteVisitRequirement}
                  label="Site Visit"
                  onChange={(e) => setSiteVisitRequirement(e.target.value)}
                >
                  <MenuItem value="recommended_before_start">Recommended before start</MenuItem>
                  <MenuItem value="required_before_quote">Already completed before pricing</MenuItem>
                  <MenuItem value="not_required">Not required</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {BID_SETUP_HELP.siteVisitRequirement[siteVisitRequirement]}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Plan</InputLabel>
                <Select
                  value={paymentStructure}
                  label="Payment Plan"
                  onChange={(e) => setPaymentStructure(e.target.value)}
                >
                  <MenuItem value="milestone_based">Milestone based</MenuItem>
                  <MenuItem value="deposit_then_completion">Deposit then completion</MenuItem>
                  <MenuItem value="monthly_progress">Monthly progress payments</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {BID_SETUP_HELP.paymentStructure[paymentStructure]}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ py: 0.75 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                  What AI will assume
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {getBidSetupSummary().map((item) => (
                    <Typography component="li" variant="caption" key={item} sx={{ mb: 0.25 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label="Notes for AI"
                placeholder="Example: include cleanup, protect existing cabinets, client wants minimal downtime..."
                value={bidNotes}
                onChange={(e) => setBidNotes(e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
            onClick={handleGenerateWithAI}
            disabled={generating || !projectType.trim() || !projectDescription.trim() || !canCreatePricedBid}
            sx={{
              background: brand.gradient,
              textTransform: 'none',
              fontWeight: 'bold',
              '&:hover': {
                background: brand.gradientHover
              }
            }}
          >
            {generating ? 'Generating...' : 'Generate Verified Quotation with AI'}
          </Button>
        </Box>
      </Box>
      </>
      )}
    </Box>
  );

  // ============================================================================
  // STEP 2 & 3: SUMMARY & SCOPE (Simplified for brevity as they are standard text fields)
  // ============================================================================

  const renderStep2ExecutiveSummary = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6">Executive Summary</Typography>
      <TextField
        fullWidth multiline rows={4}
        value={executiveSummary}
        onChange={(e) => setExecutiveSummary(e.target.value)}
        placeholder="High-level overview..."
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
         <Button
           size="small"
           startIcon={<AutoAwesome />}
           onClick={() => handleImproveSection('executive_summary', executiveSummary, setExecutiveSummary)}
           sx={{
             background: brand.gradient,
             color: 'white',
             '&:hover': {
               background: brand.gradientHover
             }
           }}
         >
            Improve with AI
         </Button>
      </Box>

      <Typography variant="h6">Project Understanding</Typography>
      <TextField
        fullWidth multiline rows={5}
        value={projectUnderstanding}
        onChange={(e) => setProjectUnderstanding(e.target.value)}
        placeholder="Detailed description..."
      />
    </Box>
  );

  const renderStep3ScopeOfWork = () => (
    <Box>
      <Typography variant="h6">Scope of Work</Typography>
      <TextField
        fullWidth
        multiline
        rows={12}
        value={scopeOfWork}
        onChange={(e) => {
          setScopeOfWork(e.target.value);
          clearFieldError('scopeOfWork');
        }}
        placeholder="Comprehensive description of all activities..."
        required
        error={!!fieldErrors.scopeOfWork}
        helperText={
          fieldErrors.scopeOfWork ||
          `${scopeOfWork.length} characters (minimum 50 required)`
        }
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
         <Button
           size="small"
           startIcon={<AutoAwesome />}
           onClick={() => handleImproveSection('scope_of_work', scopeOfWork, setScopeOfWork)}
           sx={{
             background: brand.gradient,
             color: 'white',
             '&:hover': {
               background: brand.gradientHover
             }
           }}
         >
            Improve with AI
         </Button>
      </Box>
    </Box>
  );

  // ============================================================================
  // STEP 4: INCLUSIONS & ATTACHMENTS
  // ============================================================================

  const renderStep4InclusionsExclusions = () => {
    const normalizedInclusions = normalizeSelectableItems(inclusions);
    const normalizedExclusions = normalizeSelectableItems(exclusions);
    const selectedInclusionCount = normalizedInclusions.filter((item) => item.selected).length;
    const selectedExclusionCount = normalizedExclusions.filter((item) => item.selected).length;

    return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
         <Box>
           <Typography variant="h6">Inclusions & Attachments</Typography>
           <Typography variant="body2" color="text.secondary">
             Tick only what applies to this job. Add your own items where the AI missed something.
           </Typography>
         </Box>
         <Button
           variant="contained"
           startIcon={<AutoAwesome />}
           onClick={handleGenerateInclusionsExclusions}
           disabled={loading}
           sx={{
             background: brand.gradient,
             color: 'white',
             '&:hover': {
               background: brand.gradientHover
             }
           }}
         >
            {loading ? 'Generating...' : 'Auto-Generate Lists'}
         </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Inclusions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: 'success.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">Inclusions</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedInclusionCount} selected for the bid
                </Typography>
              </Box>
              {!!normalizedInclusions.length && (
                <Button
                  size="small"
                  onClick={() => setInclusions(normalizedInclusions.map((item) => ({ ...item, selected: true })))}
                  sx={{ textTransform: 'none' }}
                >
                  Select all
                </Button>
              )}
            </Box>
            <List dense>
              {normalizedInclusions.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No inclusions yet. Generate suggestions or add your own.
                </Typography>
              )}
              {normalizedInclusions.map((item, index) => (
                <ListItem
                  key={`${item.text}-${index}`}
                  disableGutters
                  secondaryAction={
                    <IconButton
                      size="small"
                      aria-label="Remove inclusion"
                      onClick={() => removeSelectableItem(setInclusions, inclusions, index)}
                    >
                      <Delete fontSize="small"/>
                    </IconButton>
                  }
                  sx={{ alignItems: 'flex-start' }}
                >
                  <Checkbox
                    size="small"
                    edge="start"
                    checked={item.selected}
                    onChange={() => toggleSelectableItem(setInclusions, inclusions, index)}
                    inputProps={{ 'aria-label': `Include ${item.text}` }}
                    sx={{ mt: -0.5 }}
                  />
                  <ListItemText
                    primary={item.text}
                    secondary={item.custom ? 'Added by contractor' : null}
                    primaryTypographyProps={{
                      sx: {
                        textDecoration: item.selected ? 'none' : 'line-through',
                        color: item.selected ? 'text.primary' : 'text.disabled'
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                value={newInclusion}
                onChange={(e) => setNewInclusion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddInclusion();
                  }
                }}
                placeholder="Add your own inclusion..."
              />
              <IconButton onClick={handleAddInclusion} color="success"><Add /></IconButton>
            </Box>
          </Paper>
        </Grid>

        {/* Exclusions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, bgcolor: 'error.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">Exclusions</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedExclusionCount} selected for the bid
                </Typography>
              </Box>
              {!!normalizedExclusions.length && (
                <Button
                  size="small"
                  onClick={() => setExclusions(normalizedExclusions.map((item) => ({ ...item, selected: true })))}
                  sx={{ textTransform: 'none' }}
                >
                  Select all
                </Button>
              )}
            </Box>
            <List dense>
              {normalizedExclusions.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No exclusions yet. Generate suggestions or add your own.
                </Typography>
              )}
              {normalizedExclusions.map((item, index) => (
                <ListItem
                  key={`${item.text}-${index}`}
                  disableGutters
                  secondaryAction={
                    <IconButton
                      size="small"
                      aria-label="Remove exclusion"
                      onClick={() => removeSelectableItem(setExclusions, exclusions, index)}
                    >
                      <Delete fontSize="small"/>
                    </IconButton>
                  }
                  sx={{ alignItems: 'flex-start' }}
                >
                  <Checkbox
                    size="small"
                    edge="start"
                    checked={item.selected}
                    onChange={() => toggleSelectableItem(setExclusions, exclusions, index)}
                    inputProps={{ 'aria-label': `Exclude ${item.text}` }}
                    sx={{ mt: -0.5 }}
                  />
                  <ListItemText
                    primary={item.text}
                    secondary={item.custom ? 'Added by contractor' : null}
                    primaryTypographyProps={{
                      sx: {
                        textDecoration: item.selected ? 'none' : 'line-through',
                        color: item.selected ? 'text.primary' : 'text.disabled'
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                value={newExclusion}
                onChange={(e) => setNewExclusion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddExclusion();
                  }
                }}
                placeholder="Add your own exclusion..."
              />
              <IconButton onClick={handleAddExclusion} color="error"><Add /></IconButton>
            </Box>
          </Paper>
        </Grid>

        {/* Attachments */}
        <Grid item xs={12}>
           <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px dashed grey' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Documents & Attachments</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                 <Button variant="contained" component="label" startIcon={<CloudUpload />}>
                    Upload File
                    <input hidden accept="application/pdf,image/*" type="file" onChange={handleAddAttachment} />
                 </Button>
                 <Typography variant="body2" color="text.secondary">
                    Attach drawings, detailed quotes, or insurance certificates (PDF, JPG).
                 </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                 {attachments.map((file, i) => (
                    <Chip key={i} label={file.name} onDelete={() => setAttachments(attachments.filter((_, idx) => idx !== i))} />
                 ))}
              </Box>
           </Paper>
        </Grid>
      </Grid>
    </Box>
    );
  };

  // ============================================================================
  // STEP 5: COST BREAKDOWN
  // ============================================================================

  const renderCategoryLineItems = (field) => {
    const items = costLineItems[field.key] || [];
    return (
      <Box sx={{ mt: 1.5, pl: { xs: 0, sm: 1 } }}>
        {items.map((item, index) => (
          <Grid container spacing={1} key={index} sx={{ mb: 1 }} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                label="Item"
                placeholder="e.g. 600x600 porcelain tiles"
                value={item.description}
                onChange={(e) => {
                  const next = items.map((it, i) => (i === index ? { ...it, description: e.target.value } : it));
                  setCategoryItems(field.key, next);
                }}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Qty"
                type="number"
                value={item.quantity}
                onChange={(e) => {
                  const next = items.map((it, i) => (i === index ? { ...it, quantity: e.target.value } : it));
                  setCategoryItems(field.key, next);
                }}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Unit"
                placeholder="m², bag, day"
                value={item.unit}
                onChange={(e) => {
                  const next = items.map((it, i) => (i === index ? { ...it, unit: e.target.value } : it));
                  setCategoryItems(field.key, next);
                }}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Rate"
                type="number"
                value={item.priceAtPurchase ? '' : item.unitPrice}
                disabled={!!item.priceAtPurchase}
                placeholder={item.priceAtPurchase ? 'At purchase' : undefined}
                onChange={(e) => {
                  const next = items.map((it, i) => (i === index ? { ...it, unitPrice: e.target.value } : it));
                  setCategoryItems(field.key, next);
                }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: { sm: 'none' } }}>
                {item.priceAtPurchase ? 'Priced at purchase' : `= ${currency} ${lineItemTotal(item).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
              </Typography>
              <IconButton size="small" onClick={() => setCategoryItems(field.key, items.filter((_, i) => i !== index))}>
                <Delete fontSize="small" />
              </IconButton>
            </Grid>
            <Grid item xs={12} sx={{ mt: -1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!item.priceAtPurchase}
                    onChange={(e) => {
                      const next = items.map((it, i) => (i === index ? { ...it, priceAtPurchase: e.target.checked } : it));
                      setCategoryItems(field.key, next);
                    }}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    Price at purchase — I can confirm the quantity now, but the price is agreed when it's bought
                  </Typography>
                }
                sx={{ ml: 0 }}
              />
            </Grid>
          </Grid>
        ))}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="small" startIcon={<Add />} onClick={() => setCategoryItems(field.key, [...items, emptyLineItem()])}>
            Add BOQ item
          </Button>
          <Button
            size="small"
            color="inherit"
            onClick={() => setCategoryItems(field.key, null)}
          >
            Remove BOQ
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {field.label} BOQ subtotal: <strong>{currency} {categoryItemsTotal(items).toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
            {countAtPurchase(items) > 0 && ` + ${countAtPurchase(items)} item${countAtPurchase(items) === 1 ? '' : 's'} priced at purchase`}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderStep5CostBreakdown = () => {
    const visibleFields = COST_BREAKDOWN_FIELDS.filter((field) => visibleCostKeys.includes(field.key));
    const hiddenFields = COST_BREAKDOWN_FIELDS.filter((field) => !visibleCostKeys.includes(field.key));
    return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Cost Breakdown & Bill of Quantities</Typography>
        <Typography variant="body2" color="text.secondary">
          All amounts here are yours — nothing is pre-filled by AI. Enter a total per category, or click <strong>Build BOQ</strong> to itemize a category into a bill of quantities the owner can read line by line — quantities, units, and rates. Itemized categories add up automatically.
        </Typography>
      </Box>

      {Object.values(costLineItems).some((items) => (items || []).length > 0) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your itemized quantities are treated as an <strong>estimate based on the site assessment</strong> — the owner sees this note on your quotation. Actual quantities may be more or less, and additional items may be identified once work begins; any changes are agreed with the owner through change orders before extra cost is incurred, so estimate honestly rather than padding. If a price changes too often to quote, tick <strong>Price at purchase</strong> on that item — the owner is told the price will be confirmed when it's bought.
        </Alert>
      )}

      <Grid container spacing={2}>
        {visibleFields.map((field) => {
          const isItemized = Array.isArray(costLineItems[field.key]);
          return (
            <Grid item xs={12} sm={isItemized ? 12 : 6} key={field.key}>
              <Paper variant={isItemized ? 'outlined' : 'elevation'} elevation={0} sx={isItemized ? { p: 1.5 } : undefined}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label={field.label}
                    type="number"
                    value={costBreakdown[field.key] || ''}
                    disabled={isItemized}
                    onChange={(e) => updateCostBreakdown(field.key, e.target.value)}
                    helperText={isItemized ? 'Auto-summed from the items below' : undefined}
                    InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
                  />
                  {!isItemized && (
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                      onClick={() => setCategoryItems(field.key, [emptyLineItem()])}
                    >
                      Build BOQ
                    </Button>
                  )}
                </Box>
                {isItemized && renderCategoryLineItems(field)}
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {hiddenFields.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Add a category:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {hiddenFields.map((field) => (
              <Chip
                key={field.key}
                size="small"
                icon={<Add />}
                label={field.label}
                variant="outlined"
                onClick={() => setVisibleCostKeys((prev) => [...prev, field.key])}
              />
            ))}
          </Box>
        </Box>
      )}

      <Paper sx={{ p: 3, mt: 3, bgcolor: 'primary.50', textAlign: 'right' }}>
        <Typography variant="h6">Total Quotation Amount</Typography>
        <Typography variant="h4" color="primary" fontWeight="bold">
          {currency} {totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      </Paper>
    </Box>
    );
  };

  // ============================================================================
  // STEP 6 & 7: TIMELINE & TERMS
  // ============================================================================

  const renderStep6Timeline = () => (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Estimated Duration"
          type="number"
          value={estimatedDuration}
          onChange={(e) => {
            setEstimatedDuration(e.target.value);
            clearFieldError('estimatedDuration');
          }}
          error={!!fieldErrors.estimatedDuration}
          helperText={fieldErrors.estimatedDuration || 'Project duration'}
          InputProps={{
            endAdornment: (
              <Select
                variant="standard"
                value={durationUnit}
                onChange={(e) => setDurationUnit(e.target.value)}
                disableUnderline
              >
                <MenuItem value="days">Days</MenuItem>
                <MenuItem value="weeks">Weeks</MenuItem>
                <MenuItem value="months">Months</MenuItem>
              </Select>
            ),
          }}
        />
        </Grid>
        <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="date"
          label="Estimated Start Date (optional)"
          value={estimatedStartDate}
          onChange={(e) => {
            setEstimatedStartDate(e.target.value);
            clearFieldError('estimatedStartDate');
          }}
          error={!!fieldErrors.estimatedStartDate}
          helperText={fieldErrors.estimatedStartDate || 'Optional — leave blank if it depends on when the quotation is accepted'}
          InputLabelProps={{ shrink: true }}
        />
        </Grid>
      </Grid>
    </Box>
  );

  const renderPaymentStages = (total = totalCost, cur = currency) => {
    const allocated = stagesTotal(paymentStages);
    const remaining = Number((total - allocated).toFixed(2));
    return (
      <Box>
        <Typography variant="h6" gutterBottom>Payment Stages</Typography>
        {clientFundingPreference === 'stages' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>This client asked to pay in stages.</strong> Quote it stage by stage — they will fund stage 1 first and the rest as each stage is approved.
          </Alert>
        )}
        <Alert severity="success" icon={<CheckCircle fontSize="inherit" />} sx={{ mb: 2 }}>
          <strong>Stages get you the job, and protect your money.</strong> The client is told "start with" your first stage, not the whole total — that is what turns silence into a yes. Set your stages here (e.g. siting &amp; foundation $3,000, then the next stages). The client must lock each agreed stage's amount into the Eqaya project wallet <strong>before you start that stage</strong> — you can see the money is secured before you buy materials or pay workers — and it's released to you when the stage is completed and approved. No more finishing a job and chasing payment.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {total >= STAGES_REQUIRED_FROM
            ? `Quotations of ${fmtMoney(STAGES_REQUIRED_FROM, cur)} or more must be split into at least two stages, with the first stage no more than ${Math.round(FIRST_STAGE_MAX_SHARE * 100)}% of the total. The client is told "start with your stage 1 amount" instead of "${fmtMoney(total, cur)}" — that is the difference between a reply and silence. `
            : 'For a small job a single stage is fine. Splitting bigger jobs into stages is what gets clients to say yes: they fund one stage at a time. '}
          Size stages to what a client can fund at a time, and make sure they add up to your quotation total. {materialsResponsibility === 'client_supplied'
            ? 'The client supplies materials, so your stages are labor — split them by phase of work.'
            : 'You supply materials — include each stage\'s materials in that stage\'s amount so you are never buying materials from your own pocket.'}
        </Typography>

        {paymentStages.map((stage, index) => (
          <Grid container spacing={1} key={index} sx={{ mb: 1 }} alignItems="center">
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                size="small"
                label={`Stage ${index + 1} name`}
                placeholder="e.g. Demolition & prep"
                value={stage.name}
                onChange={(e) => setPaymentStages((prev) => prev.map((s, i) => (i === index ? { ...s, name: e.target.value } : s)))}
              />
            </Grid>
            <Grid item xs={8} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="What's covered (optional)"
                value={stage.description}
                onChange={(e) => setPaymentStages((prev) => prev.map((s, i) => (i === index ? { ...s, description: e.target.value } : s)))}
              />
            </Grid>
            <Grid item xs={3} sm={2}>
              <TextField
                fullWidth
                size="small"
                label="Amount"
                type="number"
                value={stage.amount}
                onChange={(e) => setPaymentStages((prev) => prev.map((s, i) => (i === index ? { ...s, amount: e.target.value } : s)))}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={1} sm={1}>
              <IconButton size="small" onClick={() => setPaymentStages((prev) => prev.filter((_, i) => i !== index))}>
                <Delete fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
          <Button size="small" startIcon={<Add />} onClick={() => setPaymentStages((prev) => [...prev, { name: '', description: '', amount: '' }])}>
            Add stage
          </Button>
          <Typography
            variant="caption"
            sx={{ ml: 'auto', fontWeight: 600, color: Math.abs(remaining) <= 0.01 && paymentStages.length > 0 ? 'success.main' : remaining < 0 ? 'error.main' : 'text.secondary' }}
          >
            Allocated: {cur} {allocated.toLocaleString('en-US', { minimumFractionDigits: 2 })} of {cur} {total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            {Math.abs(remaining) > 0.01 && ` (${remaining > 0 ? `${cur} ${remaining.toLocaleString('en-US', { minimumFractionDigits: 2 })} left to allocate` : `over by ${cur} ${Math.abs(remaining).toLocaleString('en-US', { minimumFractionDigits: 2 })}`})`}
          </Typography>
        </Box>
        {paymentStages.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {total >= STAGES_REQUIRED_FROM
              ? 'No stages yet — you won\'t be able to submit until this quotation is split into stages. Add the stages the way you would run the job — the client is asked for stage 1 first.'
              : 'No stages proposed — you\'ll agree the breakdown with the client after your quotation is accepted, and each agreed stage is funded into the project wallet before it starts.'}
          </Typography>
        )}
      </Box>
    );
  };

  const renderStep7Terms = () => (
    <Box>
       <Typography variant="h6" gutterBottom>Payment & Deposit</Typography>
       <Paper sx={{ p: 2, mb: 3, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.main' }}>
          <Typography variant="body2">
             <strong>Payment Security:</strong> To ensure safety for both parties, the project balance is held securely.
             You may request an upfront deposit for mobilization, but the remainder is released only upon job completion.
          </Typography>
       </Paper>

       <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
             <FormControl fullWidth>
                <InputLabel>Deposit Type</InputLabel>
                <Select value={depositType} label="Deposit Type" onChange={(e) => setDepositType(e.target.value)}>
                   <MenuItem value="amount">Fixed Amount ($)</MenuItem>
                   <MenuItem value="percentage">Percentage (%)</MenuItem>
                </Select>
             </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
             <TextField
                fullWidth
                label="Deposit Required"
                type="number"
                value={depositValue}
                onChange={(e) => {
                  setDepositValue(e.target.value);
                  clearFieldError('depositValue');
                }}
                error={!!fieldErrors.depositValue}
                helperText={fieldErrors.depositValue || 'Enter deposit amount'}
                InputProps={{
                   startAdornment: depositType === 'amount' ? <InputAdornment position="start">$</InputAdornment> : null,
                   endAdornment: depositType === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : null
                }}
             />
          </Grid>
       </Grid>

       <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          {totalCost > 0 ? (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Total Quotation:</Typography>
                <Typography fontWeight="bold">${totalCost.toLocaleString()}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, color: 'error.main' }}>
                <Typography>Initial Deposit (Released Upfront):</Typography>
                <Typography fontWeight="bold">${depositAmount.toLocaleString()}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', color: 'success.main' }}>
                <Typography>Balance (Released on Completion):</Typography>
                <Typography fontWeight="bold">${(totalCost - depositAmount).toLocaleString()}</Typography>
              </Box>
            </>
          ) : (
            <Typography color="text.secondary">No quotation total specified.</Typography>
          )}
       </Box>

       <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ mt: 4, fontWeight: 'bold' }}>Terms & Conditions</Typography>
          <Button
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <AutoAwesome />}
            onClick={() => handleImproveSection('project_specific_terms', projectSpecificTerms, setProjectSpecificTerms)}
            disabled={!projectSpecificTerms.trim() || loading}
            sx={{
              mt: 4,
              background: brand.gradient,
              color: 'white',
              '&:hover': {
                background: brand.gradientHover
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.26)'
              }
            }}
          >
            Improve with AI
          </Button>
       </Box>
       
       {/* Platform Standard Terms (Read-only for transparency) */}
       <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 2, maxHeight: '200px', overflowY: 'auto' }}>
          <Typography variant="caption" color="primary" fontWeight="bold" display="block" gutterBottom>
             PLATFORM STANDARD TERMS (FIXED)
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary', fontSize: '0.75rem' }}>
             {FIXED_PLATFORM_TERMS}
          </Typography>
       </Paper>

       {/* Project Specific Additions (Editable) */}
       <TextField
          fullWidth
          label="Project-Specific Additions & Operational Terms"
          placeholder="Add job-specific terms like cleanup, access hours, or material handling..."
          multiline
          rows={6}
          value={projectSpecificTerms}
          onChange={(e) => setProjectSpecificTerms(e.target.value)}
          helperText="The Platform Standard terms above will be automatically included in your final proposal."
       />
       
       <Grid container spacing={2} sx={{ mt: 1 }}>
         <Grid item xs={6}>
            <TextField fullWidth label="Warranty Period" value={warrantyPeriod} onChange={(e) => setWarrantyPeriod(e.target.value)} />
         </Grid>
         <Grid item xs={6}>
            <TextField
              fullWidth
              type="date"
              label="Quotation Valid Until"
              value={validUntil}
              onChange={(e) => {
                setValidUntil(e.target.value);
                clearFieldError('validUntil');
              }}
              error={!!fieldErrors.validUntil}
              helperText={fieldErrors.validUntil || 'Date until which this quotation is valid'}
              InputLabelProps={{ shrink: true }}
            />
         </Grid>
       </Grid>
    </Box>
  );


  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={isMobile ? false : "md"}
      fullWidth={!isMobile}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          minHeight: isMobile ? '100vh' : '85vh',
          maxHeight: isMobile ? '100vh' : '90vh',
          display: 'flex',
          flexDirection: 'column'
        },
      }}
    >
      <DialogCloseButton onClick={handleClose} sx={{ color: 'white' }} />
      <DialogTitle
        sx={{
          background: brand.gradient,
          color: 'white',
          p: isMobile ? 2 : 3,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2 }}>
            <AutoAwesome sx={{ fontSize: isMobile ? 24 : 32 }} />
            <Box>
              <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, letterSpacing: 1 }}>
                {uploadMode ? 'Send Your Own Quotation' : isEditMode ? 'Edit Quotation' : 'Build Professional Quotation'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {uploadMode
                  ? 'Your letterhead, your document — accepted and paid through Eqaya'
                  : isEditMode ? 'Update Your Quotation' : 'Request details first, then draft a contractor-reviewed quotation'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {savedDraft && (
              <Chip
                label="Browser Draft Saved"
                size="small"
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600,
                }}
                icon={<CheckCircle sx={{ color: 'white !important' }} />}
              />
            )}
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pb: 10 }}>
        {uploadMode ? renderUploadQuotation() : (
        <>
        <Stepper
          activeStep={activeStep}
          alternativeLabel={!isMobile}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{
            mb: 4,
            px: isMobile ? 1 : 0,
            '& .MuiStepLabel-root .Mui-completed': {
              color: brand.primary,
            },
            '& .MuiStepLabel-root .Mui-active': {
              color: brand.primary,
            },
            '& .MuiStepIcon-root.Mui-active': {
              color: brand.primary,
            },
            '& .MuiStepIcon-root.Mui-completed': {
              color: brand.primary,
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {showBidGuide && !isEditMode && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            onClose={() => {
              setShowBidGuide(false);
              localStorage.setItem('eqaya_contractor_bid_guide_seen', 'true');
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              How AI Quotation Creation Works
            </Typography>
            <Typography variant="body2" component="div">
              Confirm the project, answer a few contractor questions, then let AI draft the quotation for you:
              <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li><strong>Check readiness</strong> — confirm site visit, photos, measurements, and material choices</li>
                <li><strong>Generate the draft</strong> — AI writes the summary, scope, assumptions, and terms. Prices are never AI-generated — you enter every amount yourself</li>
                <li><strong>Review like a contractor</strong> — adjust price, timeline, exclusions, and deposit before preview</li>
                <li><strong>Save or submit</strong> — the client only sees it after you approve the final document</li>
              </ol>
            </Typography>
          </Alert>
        )}

        {renderStepContent()}
        </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          p: 2,
          justifyContent: 'space-between',
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
          zIndex: 1
        }}
      >
        {uploadMode ? (
          <>
            <Button onClick={() => setUploadMode(false)} disabled={loading} startIcon={<ArrowBack />}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadQuotationSubmit}
              disabled={loading || !uploadReady}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
              sx={{ background: brand.gradient, '&:hover': { background: brand.gradientHover } }}
            >
              {loading ? 'Sending…' : 'Send Quotation to Client'}
            </Button>
          </>
        ) : (
        <>
        <Button onClick={handleBack} disabled={activeStep === 0} startIcon={<ArrowBack />}>
          Back
        </Button>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => saveServerDraft()}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            endIcon={<ArrowForward />}
            disabled={!isEditMode && activeStep === 0 && !pricingDecision}
          >
            {activeStep === steps.length - 1 ? 'Preview Quotation' : 'Next'}
          </Button>
        </Box>
        </>
        )}
      </DialogActions>
    </Dialog>

    {/* Full Screen Preview Modal */}
    {showPreview && (
      <BidDocumentPreview
        open={showPreview}
        onClose={() => setShowPreview(false)}
        onSubmit={handleSubmitBid}
        onSubmitToClient={isEditMode ? undefined : handleSubmitBidToClient}
        loading={loading}
        isEditMode={isEditMode}
        contractor={user} // Pass auth user as contractor details
        data={{
           bidIntent,
           projectType,
           executiveSummary,
           scopeOfWork,
           inclusions: getSelectedItemTexts(inclusions),
           exclusions: getSelectedItemTexts(exclusions),
           attachments,
           costBreakdown: buildCostBreakdownPayload(),
           paymentSchedule: buildPaymentSchedulePayload(),
           totalCost,
           depositAmount,
           estimatedDuration,
           durationUnit,
           estimatedStartDate,
           termsAndConditions: fullTerms,
           warrantyPeriod,
           validUntil
        }}
        onEditSection={handlePreviewEdit}
      />
    )}

    {/* Close Confirmation Dialog */}
    <Dialog
      open={closeDialogOpen}
      onClose={() => setCloseDialogOpen(false)}
    >
      <DialogCloseButton onClick={() => setCloseDialogOpen(false)} />
      <DialogTitle>Close Quotation Builder?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Closing will keep a private browser recovery draft, but it will not save a quotation to your My Requests & Quotes tab. Use Save Draft when you want it added to your quotation queue.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCloseDialogOpen(false)}>Keep Editing</Button>
        <Button onClick={handleCloseConfirm} color="primary" variant="contained">Close</Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={handleSnackbarClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
        {snackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
};

export default AIBidGenerator;
